/*
  <pz-builder> — necklace builder page (sections/pz-builder.liquid), 2026-10-02.

  The customer picks a chain, any number of charms, a material and a length. The necklace is drawn live on a canvas
  from prepared parts — nothing is generated on the fly:
    pz-z-<chain>-<metal>.webp   a short piece of chain that tiles seamlessly; it is laid along the necklace curve
    pz-c-<charm>-<metal>.webp   a charm with its own bail or ring; the chain passes through (or behind) it
    pz-s-<charm>-<month>.webp   the stone of a birthstone charm, drawn over the charm
  Every part is at the same scale (data.pxmm pixels per millimetre), so sizes stay true to each other.
  Name plates, initials and numbers are drawn from the customer's own text in the chosen font; engravable charms
  take a line of text or an uploaded image, engraved with GravurMotor (assets/gravur-motor.js, the engraved coin's
  engine). The catalogue (snippets/pz-data.liquid) holds no prices: every chain and charm is a real variant, matched
  by SKU, so the total shown here is what the cart charges: chain + all charms. Material and length never change it.

  ADD TO BAG posts the chain and one line per charm in a single request, tied together by "_Design ID".
*/
(() => {
  if (customElements.get('pz-builder')) return;

  const KAYIT = 'pz-design-v1';
  const ZEMIN = '#f1ece5';
  // Tones of our flat cut-out parts (the lotus, the wing), measured on the part photos by distance from the edge:
  // an even bright face, and a narrow edge that is near white where it faces the light (upper left) and deep brown
  // where it faces away. Silver and rose come from the parts' own colour transform.
  const TON = {
    gold: { ust: [247, 223, 180], alt: [240, 212, 167], yan: [204, 174, 132], isik: [255, 249, 232], golge: [128, 90, 46] },
    silver: { ust: [230, 231, 233], alt: [220, 222, 224], yan: [185, 186, 188], isik: [251, 252, 252], golge: [105, 106, 108] },
    rose: { ust: [247, 209, 193], alt: [237, 196, 179], yan: [193, 154, 138], isik: [255, 244, 238], golge: [103, 61, 43] },
  };
  const GRUPLAR = [['all', 'All'], ['birthstones', 'Birthstones'], ['names', 'Names'], ['initials', 'Initials & Numbers'], ['engraving', 'Photo Engraving'], ['symbols', 'Symbols']];
  const ORNEK_TAS = { 'bs-round': 'may', 'bs-pear': 'dec', 'bs-pear-halo': 'jul', 'bs-baguette': 'feb', 'bs-marquise': 'oct' };
  // the scripts of the Arabic and the Hebrew name plates (a name typed in other letters is written by the workshop)
  const BETIK = { ar: /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/, he: /[\u0590-\u05FF\uFB1D-\uFB4F]/ };
  const STILLER = [['cizgi', 'Line Art'], ['foto', 'Photo'], ['damga', 'Stamp']];
  // birth flower bouquet: per number of flowers, where each head sits, [x, y] mm from the meeting point of the stems
  const BUKET = { 1: [[0, -7.4]], 2: [[-2.4, -10.2], [2.4, -7]], 3: [[0, -11], [-2.5, -6.8], [2.5, -6.8]], 4: [[-2.5, -11.2], [2.5, -11.2], [-2.6, -6.8], [2.6, -6.8]],
    5: [[0, -15.2], [-2.5, -11.2], [2.5, -11.2], [-2.6, -6.8], [2.6, -6.8]], 6: [[-2.5, -15.4], [2.5, -15.4], [-2.6, -11.2], [2.6, -11.2], [-2.6, -7], [2.6, -7]] };
  const BUST = { 14: [9, 15], 16: [12.5, 21], 18: [13.5, 26], 20: [14, 31], 22: [14.5, 36] };

  const rgba = (c, a = 1) => `rgba(${c[0]},${c[1]},${c[2]},${a})`;
  const kacis = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const tuval = (w, h) => { const c = document.createElement('canvas'); c.width = Math.max(1, Math.round(w)); c.height = Math.max(1, Math.round(h)); return c; };
  const blob = (cv, tur, kalite) => new Promise((ok) => cv.toBlob(ok, tur, kalite));
  const kimlik = () => Math.random().toString(36).slice(2, 10);
  const sinir = (v, a, b) => Math.min(b, Math.max(a, v));

  class PzBuilder extends HTMLElement {
    connectedCallback() {
      if (this._hazir) return; this._hazir = true;
      const q = (s) => this.querySelector(s);
      this.veri = JSON.parse(q('[data-pz-data]').textContent);
      this.varyant = JSON.parse(q('[data-pz-varyant]').textContent);
      this.PXMM = this.veri.pxmm;
      // Only what can actually be bought is offered: a catalogue entry without a variant stays hidden.
      this.zincirler = this.veri.chains.filter((z) => this.varyant[z.sku]);
      this.uclar = this.veri.charms.filter((u) => this.varyant[u.sku]);
      this.zincirById = Object.fromEntries(this.zincirler.map((z) => [z.id, z]));
      this.ucById = Object.fromEntries(this.uclar.map((u) => [u.id, u]));
      this.gorseller = new Map();
      this.seritler = new Map();
      this.filtre = 'all';
      this.acik = 'uc';
      this.tv = q('[data-tuval]');
      if (!this.zincirler.length) { q('.pz__yukleniyor').textContent = this.dataset.kapaliText || 'The builder is not available right now.'; return; }

      this.durum = this.yukle();
      this.addEventListener('click', (e) => this.tikla(e));
      this.addEventListener('input', (e) => this.girdi(e));
      this.addEventListener('change', (e) => this.degisti(e));
      this.sahneBagla();
      new ResizeObserver(() => this.ciz()).observe(this.tv);
      // the stage takes what the header above and the summary box below leave of the window, so the total and the
      // button never fall under it
      const ozet = q('.pz__ozet'), duzen = q('.pz__duzen');
      const olc = () => {
        this.style.setProperty('--pz-ozet-yuk', `${Math.ceil(ozet.getBoundingClientRect().height)}px`);
        this.style.setProperty('--pz-ust', `${Math.round(duzen.getBoundingClientRect().top + window.scrollY)}px`);
      };
      new ResizeObserver(olc).observe(ozet);
      window.addEventListener('resize', olc);
      window.addEventListener('load', olc);
      if (window.matchMedia('(max-width: 999px)').matches) q('.pz__dokum')?.setAttribute('open', '');

      this.alanlariKur();
      this.bolumleriKur();
      this.parcalariKur();
      this.guncelle();
      document.fonts?.ready.then(() => this.ciz());
    }

    // ——————————————————————————————————————————————— state

    varsayilan() {
      const z = this.zincirler[0];
      return { zincir: z.id, metal: this.veri.metals[0].id, boy: z.lengths.includes(18) ? 18 : z.lengths[0], parcalar: [] };
    }

    // A reload keeps the design (uploaded images are not stored; those charms ask for their image again).
    yukle() {
      const d = this.varsayilan();
      try {
        const k = JSON.parse(localStorage.getItem(KAYIT) || 'null');
        if (k && this.zincirById[k.zincir]) d.zincir = k.zincir;
        if (k && this.veri.metals.some((m) => m.id === k.metal)) d.metal = k.metal;
        if (k && Number(k.boy)) d.boy = Number(k.boy);
        for (const p of k?.parcalar || []) if (this.ucById[p.uc]) d.parcalar.push({ uid: kimlik(), uc: p.uc, deger: { ...this.bosDeger(this.ucById[p.uc]), ...(p.deger || {}) }, ...(Number.isFinite(p.konum) ? { konum: p.konum } : {}) });
      } catch { /* a broken or blocked storage just means a fresh design */ }
      d.boy = this.yakinBoy(this.zincirById[d.zincir], d.boy);
      return d;
    }

    kaydet() {
      const d = this.durum;
      try { localStorage.setItem(KAYIT, JSON.stringify({ zincir: d.zincir, metal: d.metal, boy: d.boy, parcalar: d.parcalar.map((p) => ({ uc: p.uc, deger: p.deger, ...(p.konum != null ? { konum: p.konum } : {}) })) })); } catch { /* private mode */ }
    }

    bosDeger(uc) {
      const deger = {};
      for (const a of uc.fields) { deger[a.key] = a.type === 'birthstones' ? [''] : a.type === 'birthflowers' ? ['', '', ''] : ''; if (a.font) deger.font = uc.text?.defaultFont || this.veri.fonts[0].id; }
      return deger;
    }

    yakinBoy(z, boy) {
      return z.lengths.reduce((en, b) => (Math.abs(b - boy) < Math.abs(en - boy) ? b : en), z.lengths[0]);
    }

    fiyat(sku) { return this.varyant[sku]?.p || 0; }

    // a charm priced by its number of stones has a variant per count: <sku>-<n> for n stones (the plain sku is one stone)
    ucSku(p) {
      const u = this.ucById[p.uc], a = u.fields.find((x) => x.type === 'birthstones'), n = a ? (p.deger[a.key] || []).length : 1;
      return n > 1 && this.varyant[`${u.sku}-${n}`] ? `${u.sku}-${n}` : u.sku;
    }

    toplam() {
      const d = this.durum;
      return this.fiyat(this.zincirById[d.zincir].sku) + d.parcalar.reduce((t, p) => t + this.fiyat(this.ucSku(p)), 0);
    }

    para(kurus) {
      const n = Number(kurus) / 100;
      const bicim = (ondalik, binlik, ayrac) => {
        const [a, b] = n.toFixed(ondalik).split('.');
        const tam = a.replace(/\B(?=(\d{3})+(?!\d))/g, binlik);
        return b ? tam + ayrac + b : tam;
      };
      const tablo = {
        amount: () => bicim(2, ',', '.'), amount_no_decimals: () => bicim(0, ',', '.'),
        amount_with_comma_separator: () => bicim(2, '.', ','), amount_no_decimals_with_comma_separator: () => bicim(0, '.', ','),
        amount_with_apostrophe_separator: () => bicim(2, "'", '.'), amount_with_space_separator: () => bicim(2, ' ', ','),
      };
      return (this.dataset.para || '{{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) => (tablo[k] || tablo.amount)());
    }

    // What a charm says in the summary, the cart and the order.
    ayrinti(p) {
      const uc = this.ucById[p.uc], d = p.deger, parca = [];
      for (const a of uc.fields) {
        if (a.type === 'birthstones') { const ts = (d[a.key] || []).filter(Boolean).map((id) => this.tasAdi(id)); if (ts.length) parca.push(ts.join(', ')); continue; }
        if (a.type === 'birthflower') { if (d[a.key]) parca.push(this.cicekAdi(d[a.key])); continue; }
        if (a.type === 'birthflowers') { const fs = (d[a.key] || []).filter(Boolean).map((id) => this.cicekAdi(id)); if (fs.length) parca.push(fs.join(', ')); continue; }
        if (a.type === 'birthstone' && d[a.key]) { const t = this.veri.stones.find((s) => s.id === d[a.key]); parca.push(`${t.month} – ${t.name}`); }
        else if (a.type === 'text' && d[a.key]?.trim()) parca.push(`“${d[a.key].trim()}”`);
        else if (a.type === 'letter' && d[a.key]) parca.push(`“${d[a.key]}”`);
        else if (a.type === 'number' && d[a.key]) parca.push(`“${d[a.key]}”`);
        else if (a.type === 'engrave-image' && p.gravur?.gorsel) parca.push(`${STILLER.find((s) => s[0] === p.gravur.ayar.stil)[1]} engraving`);
        if (a.font && d[a.key]) parca.push(this.fontAdi(d.font));
      }
      return parca.join(' · ');
    }

    fontAdi(id) { const f = this.veri.fonts.find((x) => x.id === id); return f ? `${f.name} (No. ${f.no})` : ''; }
    tasAdi(id) { const t = this.veri.stones.find((x) => x.id === id); return t ? `${t.month} – ${t.name}` : ''; }
    cicekAdi(id) { const f = (this.veri.flowers || []).find((x) => x.id === id); return f ? `${f.month} – ${f.name}` : ''; }

    eksik(p) {
      const uc = this.ucById[p.uc];
      for (const a of uc.fields) {
        if (a.type === 'engrave-image') { if (!p.gravur?.gorsel) return 'Please upload the image you would like engraved.'; continue; }
        if (a.type === 'birthstones') { const v = p.deger[a.key] || []; if (!v.length || v.some((x) => !x)) return v.length > 1 ? 'Please choose a birthstone for every stone.' : 'Please choose a birthstone.'; continue; }
        if (a.type === 'birthflowers') { const v = p.deger[a.key] || []; if (!v.length || v.some((x) => !x)) return v.length > 1 ? 'Please choose a birth flower for every flower.' : 'Please choose a birth flower.'; continue; }
        if (!String(p.deger[a.key] || '').trim()) return { birthstone: 'Please choose a birthstone.', text: `Please enter ${/name/i.test(a.label) ? 'a name' : 'your text'}.`, letter: 'Please choose a letter.', number: 'Please enter a number.', birthflower: 'Please choose a birth flower.' }[a.type];
      }
      return '';
    }

    // ——————————————————————————————————————————————— assets

    adres(dosya) { return `${this.dataset.assetBase}${dosya}?v=${this.veri.v}`; }

    gorsel(dosya) {
      if (!this.gorseller.has(dosya)) {
        this.gorseller.set(dosya, new Promise((ok, hata) => { const i = new Image(); i.crossOrigin = 'anonymous'; i.onload = () => ok(i); i.onerror = () => hata(new Error(dosya)); i.src = this.adres(dosya); }));
      }
      return this.gorseller.get(dosya);
    }

    // The chain tile repeated into one long strip (cached per chain + metal, grown when a longer one is needed).
    async serit(z, metal, uzunlukPx) {
      const anahtar = `${z.id}|${metal}`; let s = this.seritler.get(anahtar);
      if (s && s.width >= uzunlukPx) return s;
      const im = await this.gorsel(`pz-z-${z.id}-${metal}.webp`);
      s = tuval(Math.ceil((uzunlukPx * 1.3) / z.w + 1) * z.w, z.h);
      const c = s.getContext('2d');
      for (let x = 0; x < s.width; x += z.w) c.drawImage(im, x, 0, z.w, z.h);
      this.seritler.set(anahtar, s);
      return s;
    }

    // ——————————————————————————————————————————————— charm sprites (one canvas per charm on the chain)

    spriteAnahtari(p, metal = this.durum.metal) {
      const plaka = this.ucById[p.uc]?.text?.kind === 'plate';                // a plate's rings follow the chain
      return [p.uc, metal, JSON.stringify(p.deger), p.gravur?.surum || 0, plaka ? this.durum.zincir : ''].join('|');
    }

    async sprite(p, metal = this.durum.metal) {
      const uc = this.ucById[p.uc];
      const anahtar = this.spriteAnahtari(p, metal);
      if (p._sprite?.anahtar === anahtar) return p._sprite;
      const ciz = { text: this.yaziSprite, harf: this.harfSprite, yigin: this.yiginSprite, yuva: this.yuvaSprite, cember: this.cemberSprite, buket: this.buketSprite }[uc.render] || this.gorselSprite;
      const s = await ciz.call(this, uc, p, metal);
      // how the piece sits among its neighbours (letters spaced along the chain, a fan of marquises, joined letters)
      for (const k of ['aralik', 'tekAralik', 'yelpaze', 'bitisik', 'zincirsiz']) if (uc[k] != null && s[k] == null) s[k] = uc[k];
      s.anahtar = anahtar; p._sprite = s;
      return s;
    }

    async gorselSprite(uc, p, metal) {
      const im = await this.gorsel(`pz-c-${uc.id}-${metal}.webp`);
      const cv = tuval(uc.w, uc.h), c = cv.getContext('2d', { willReadFrequently: Boolean(uc.yazi || uc.gravur) });
      c.drawImage(im, 0, 0, uc.w, uc.h);
      const tas = p.deger.stone;
      if (uc.tas && tas && tas !== 'apr') c.drawImage(await this.gorsel(`pz-s-${uc.id}-${tas}.webp`), uc.tas.x, uc.tas.y, uc.tas.w, uc.tas.h);
      const tas2 = p.deger.stone2;                                    // a second stone of its own month (toi et moi)
      if (uc.tas2 && tas2 && tas2 !== 'apr') c.drawImage(await this.gorsel(`pz-s2-${uc.id}-${tas2}.webp`), uc.tas2.x, uc.tas2.y, uc.tas2.w, uc.tas2.h);
      // what is engraved: the text, the letter and the number fields, one line each (a football: name over number)
      const yazilacak = [p.deger.text, p.deger.letter, p.deger.number].map((v) => String(v ?? '').trim()).filter(Boolean).join('\n');
      if (uc.yazi && yazilacak && window.GravurMotor) {
        if (uc.yazi.font) await document.fonts?.load(`48px "PZ ${uc.yazi.font}"`, yazilacak).catch(() => {});
        this.yaziKazi(c, uc, yazilacak, metal);
      }
      if (uc.harf && p.deger.letter) await this.harfBindir(c, uc, p.deger.letter, metal);
      if (uc.kazi && p.deger.flower && window.GravurMotor) await this.cicekKazi(c, uc, p.deger.flower, metal);
      if (uc.gravur && p.gravur?.gorsel && window.GravurMotor) this.gorselKazi(c, uc, p, metal);
      return { cv, w: uc.w, h: uc.h, bag: uc.bag, boncuk: Boolean(uc.boncuk), istasyon: Boolean(uc.istasyon), halkalar: uc.halkalar };      // floating pearls touch like beads
    }

    // A charm is threaded straight onto the chain when its own ring or bail is wide enough for it. On a heavier chain it
    // is not: like a jeweller, we then hang it from a connector ring that fits the chain (the chain runs through the
    // connector, the charm hangs from its lower wire). Slides and name plates stay as they are. Openings in millimetres.
    async asil(s, uc, z, metal) {
      const h = this.veri.hoop, b = s && s.bag, kal = z.lineMm || z.mm, PX = this.PXMM;
      if (!h || !b || s.plaka || b.tip === 'kay') return s;
      const acik = Math.max(0.85, b.tip === 'halka' ? (b.delik[3] - b.delik[1]) / PX : (uc && uc.acik) || 0.85);
      if (acik >= kal * 1.08) return s;
      const anahtar = `${s.anahtar || ''}|${z.id}|${metal}`;
      if (s._asil?.anahtar === anahtar) return s._asil;
      const im = await this.gorsel(`pz-c-hoop-${metal}.webp`);
      const f = ((kal * 1.3 + 0.35) * PX) / (h.delik[3] - h.delik[1]);      // the connector's opening: the chain and some play
      const hx = ((h.delik[0] + h.delik[2]) / 2) * f, tel = h.tel * f, alt = h.delik[3] * f + tel / 2;   // middle, wire, lower wire
      // the charm's own ring or bail goes round the connector's lower wire, as it would round a chain that thin
      const ay = b.tip === 'halka' ? (tel >= b.delik[3] - b.delik[1] ? (b.delik[1] + b.delik[3]) / 2 : b.y0 + tel / 2 + 1) : b.y0 + tel / 2;
      const cx = Math.max(hx, b.x), hy = Math.max(0, ay - alt), oy = hy + alt - ay;
      const W = Math.ceil(cx + Math.max(h.w * f - hx, s.w - b.x)), H = Math.ceil(Math.max(hy + h.h * f, oy + s.h));
      const cv = tuval(W, H), c = cv.getContext('2d');
      c.imageSmoothingQuality = 'high';
      const halkaCiz = () => c.drawImage(im, cx - hx, hy, h.w * f, h.h * f);
      halkaCiz();
      c.drawImage(s.cv, cx - b.x, oy, s.w, s.h);
      if (b.tip === 'halka') {          // linked rings: the connector's wire runs in front of the right side of the charm's ring
        c.save(); c.beginPath(); c.rect(cx, hy + alt - tel * 1.6, b.dis[1] - b.x + 3, tel * 3.2); c.clip(); halkaCiz(); c.restore();
      }
      const dx = cx - hx;
      return (s._asil = { cv, w: W, h: H, yazi: s.yazi, aralik: s.aralik, anahtar, baglanti: true,
        bag: { tip: 'halka', x: cx, y0: hy + h.delik[1] * f, delik: [dx + h.delik[0] * f, hy + h.delik[1] * f, dx + h.delik[2] * f, hy + h.delik[3] * f], dis: [dx + h.dis[0] * f, dx + h.dis[1] * f] } });
    }

    // A line of text engraved on the charm. GravurMotor.isle() engraves inside an ellipse, so the text box is
    // inscribed in one: the box takes the middle 1/√2 of the engraving map.
    yaziKazi(c, uc, metin, metal) {
      const y = uc.yazi, n = 720, oran = 1 / Math.SQRT2;
      const bw = Math.max(8, Math.round(y.rx * 2 * 3)), bh = Math.max(8, Math.round(y.ry * 2 * 3));   // 3× for clean edges
      const t = tuval(bw, bh), tc = t.getContext('2d');
      const satirlar = metin.split('\n'), sh = bh / satirlar.length, aile = y.font ? `"PZ ${y.font}", Georgia, serif` : 'Arial, Helvetica, sans-serif';
      let boyut = sh * (satirlar.length > 1 ? 0.72 : 0.8);
      tc.font = `${boyut}px ${aile}`;
      const gen = Math.max(...satirlar.map((s) => tc.measureText(s).width));
      if (gen > bw * 0.94) boyut *= (bw * 0.94) / gen;
      tc.font = `${boyut}px ${aile}`; tc.textAlign = 'center'; tc.textBaseline = 'middle'; tc.fillStyle = '#000';
      satirlar.forEach((s, i) => tc.fillText(s, bw / 2, sh * (i + 0.5) + boyut * 0.04));
      const k = tuval(n, n), kc = k.getContext('2d', { willReadFrequently: true });
      kc.imageSmoothingQuality = 'high';
      kc.drawImage(t, (n * (1 - oran)) / 2, (n * (1 - oran)) / 2, n * oran, n * oran);
      const d = kc.getImageData(0, 0, n, n).data, E = new Float32Array(n * n);
      for (let i = 0; i < E.length; i++) E[i] = d[i * 4 + 3] / 255;
      E.boyut = n;
      const img = c.getImageData(0, 0, uc.w, uc.h);
      window.GravurMotor.isle(img, { cx: y.cx, cy: y.cy, rx: y.rx * Math.SQRT2, ry: y.ry * Math.SQRT2, aci: y.aci || 0 }, E, metal);
      c.putImageData(img, 0, 0);
    }

    // A letter photographed on its own (bubble, block and pavé letters): one picture per letter and metal. The letter not
    // chosen yet shows as the sample, faintly, like the placeholder of a name.
    async harfSprite(uc, p, metal) {
      const anahtar = uc.secim || 'letter', ham = String(p.deger[anahtar] || ''), secili = anahtar === 'letter' ? ham.toUpperCase() : ham;
      const L = uc.harfler[secili] ? secili : uc.harfler[uc.ornek] ? uc.ornek : Object.keys(uc.harfler)[0];
      const h = uc.harfler[L], im = await this.gorsel(`pz-c-${uc.id}-${L}-${metal}.webp`);
      const cv = tuval(h.w, h.h), c = cv.getContext('2d');
      if (!uc.harfler[secili] && !String(p.uid || '').startsWith('k-')) c.globalAlpha = 0.4;
      c.drawImage(im, 0, 0, h.w, h.h);
      return { cv, w: h.w, h: h.h, bag: h.bag, boncuk: Boolean(uc.boncuk), istasyon: Boolean(uc.istasyon), halkalar: h.halkalar };
    }

    // The chosen birth flower engraved on the oval disc: the cut-out flower's own outline (without its ring) becomes the
    // engraving, fitted inside the disc's face. The map is square while the face is an ellipse: the flower is drawn
    // pre-squeezed so it keeps its proportions once laid on the face.
    // The birth flower engraved on the disk: the fine line drawing of that month's flower (the drawings of the source's
    // flower chart, Enos 122: an engraved outline, not a filled shape). Without them, the cut flower's own shape.
    async cicekKazi(c, uc, ay, metal) {
      const f = this.ucById['flower-stem']; if (!f?.harfler?.[ay]) return;
      const cz = this.veri.flowerLines?.[ay];
      const h = f.harfler[ay], im = await this.gorsel(cz ? `pz-cz-${ay}.webp` : `pz-c-flower-stem-${ay}-gold.webp`);
      const ust = cz ? 0 : Math.min(h.h * 0.3, (h.bag.delik?.[3] ?? 0) + h.h * 0.03);        // below the ring
      const kw = cz ? cz[0] : h.w, kh = cz ? cz[1] : h.h - ust, z = uc.kazi;
      const boy = Math.min((z.ry * 2) * 0.82, ((z.rx * 2) * 0.72) * (kh / kw)), en = boy * (kw / kh);   // sprite px on the face
      const n = 640, E = tuval(n, n), ec = E.getContext('2d', { willReadFrequently: true });
      const sx = n / (2 * z.rx), sy = n / (2 * z.ry);
      ec.drawImage(im, 0, ust, kw, kh, n / 2 - (en / 2) * sx, n / 2 - (boy / 2) * sy, en * sx, boy * sy);
      const d = ec.getImageData(0, 0, n, n).data, M = new Float32Array(n * n);
      for (let i = 0; i < M.length; i++) M[i] = d[i * 4 + 3] / 255;
      M.boyut = n;
      const img = c.getImageData(0, 0, c.canvas.width, c.canvas.height);
      window.GravurMotor.isle(img, { cx: z.cx, cy: z.cy, rx: z.rx, ry: z.ry }, M, metal);
      c.putImageData(img, 0, 0);
    }

    // A small round stone set flush in the metal (family stones): a darker seat, a thin bezel, then the stone itself —
    // our round birthstone's own cut stone in that month's colour (April, or a stone not chosen yet: the clear one).
    async tasKoy(c, [x, y], cap, id, metal) {
      const R = this.ucById['bs-round'], r = cap / 2, ton = TON[metal] || TON.gold;
      c.save();
      c.beginPath(); c.arc(x, y, r * 1.2, 0, Math.PI * 2); c.fillStyle = rgba(ton.golge, 0.45); c.fill();
      c.beginPath(); c.arc(x, y, r * 1.1, 0, Math.PI * 2); c.fillStyle = rgba(ton.yan); c.fill();
      c.beginPath(); c.arc(x, y, r, 0, Math.PI * 2); c.clip();
      if (R?.tas) {
        const t = R.tas;
        if (id && id !== 'apr') c.drawImage(await this.gorsel(`pz-s-bs-round-${id}.webp`), x - r * 1.06, y - r * 1.06, cap * 1.06, cap * 1.06);
        else c.drawImage(await this.gorsel(`pz-c-bs-round-${metal}.webp`), t.x, t.y, t.w, t.h, x - r * 1.06, y - r * 1.06, cap * 1.06, cap * 1.06);
      }
      c.restore();
    }

    // Family stones stacked corner to corner in a zigzag (Enos 7): one picture of a single stone unit, drawn once per
    // stone, each with its own month; the jump ring through the first stone's top corner.
    async yiginSprite(uc, p, metal) {
      const a = uc.fields.find((x) => x.type === 'birthstones'), v = (p.deger[a.key] || ['']).slice(0, a.max), n = Math.max(1, v.length);
      const B = uc.birim, dx = B.w * uc.adim[0], dy = B.h * uc.adim[1];
      const halka = this.veri.ring, him = await this.gorsel(`pz-c-ring-${metal}.webp`);
      const taban = await this.gorsel(`pz-c-${uc.id}-${metal}.webp`);
      const taslar = await Promise.all(v.map((id) => (id && id !== 'apr' ? this.gorsel(`pz-s-${uc.id}-${id}.webp`) : null)));
      // adim[2] 'kayan': each stone a little further to one side, a slanted stair (Enos 7); otherwise right, left, right ...
      const kayan = uc.adim[2] === 'kayan', sutun = (i) => (kayan ? i * dx : n > 1 && i % 2 === 0 ? dx : 0);
      const binme = halka.h - halka.bag.delik[3] + 3, ustY = halka.h - binme;
      const W = Math.ceil(B.w + (n > 1 ? (kayan ? dx * (n - 1) : dx) : 0)), H = Math.ceil(ustY + dy * (n - 1) + B.h);
      const cv = tuval(W, H), c = cv.getContext('2d'); c.imageSmoothingQuality = 'high';
      const hx = sutun(0) + (B.tepe ? B.tepe[0] : B.w / 2) - halka.bag.x;            // the ring on the first stone's top corner
      c.drawImage(him, hx, 0, halka.w, halka.h);
      for (let i = 0; i < n; i++) {
        const x = sutun(i), y = ustY + dy * i;
        c.drawImage(taban, x, y, B.w, B.h);
        if (taslar[i]) c.drawImage(taslar[i], x + B.tas.x, y + B.tas.y, B.tas.w, B.tas.h);
      }
      const b = halka.bag;
      return { cv, w: W, h: H, bag: { ...b, x: b.x + hx, delik: [b.delik[0] + hx, b.delik[1], b.delik[2] + hx, b.delik[3]], dis: [b.dis[0] + hx, b.dis[1] + hx] } };
    }

    // Stones set along a band (the infinity cross, Enos 96): spaced evenly round the middle of uc.yol.
    async yuvaSprite(uc, p, metal) {
      const s = await this.gorselSprite(uc, p, metal), c = s.cv.getContext('2d');
      const a = uc.fields.find((x) => x.type === 'birthstones'), v = (p.deger[a.key] || ['']).slice(0, a.max);
      const yol = uc.yol, uz = [0];
      for (let i = 1; i < yol.length; i++) uz.push(uz[i - 1] + Math.hypot(yol[i][0] - yol[i - 1][0], yol[i][1] - yol[i - 1][1]));
      const L = uz[uz.length - 1];
      const nokta = (t) => { let i = 1; while (i < yol.length - 1 && uz[i] < t) i++; const f = sinir((t - uz[i - 1]) / ((uz[i] - uz[i - 1]) || 1), 0, 1); return [yol[i - 1][0] + (yol[i][0] - yol[i - 1][0]) * f, yol[i - 1][1] + (yol[i][1] - yol[i - 1][1]) * f]; };
      const n = v.length, adim = n > 1 ? Math.min(uc.tasAdim || Infinity, L / (n - 1)) : 0, bas = L / 2 - (adim * (n - 1)) / 2;
      for (let k = 0; k < n; k++) await this.tasKoy(c, nokta(bas + adim * k), uc.tasCap, v[k], metal);
      return s;
    }

    // A flat open circle (Enos 20): the names engraved round it and the stones set between them, taking turns from the
    // upper right; the top, where it hangs, stays free. Names on the upper half read clockwise with their tops outward,
    // on the lower half counter-clockwise with their tops inward, so all of them stand upright for the reader.
    async cemberSprite(uc, p, metal) {
      const s = await this.gorselSprite(uc, p, metal), c = s.cv.getContext('2d', { willReadFrequently: true });
      const a = uc.fields.find((x) => x.type === 'birthstones');
      const isimler = String(p.deger.text || '').split(/[,&+\/]|\s+and\s+/i).map((x) => x.trim()).filter(Boolean).slice(0, 4);
      const taslar = a ? (p.deger[a.key] || []).slice(0, a.max) : [];
      const ogeler = [];
      for (let i = 0; i < Math.max(isimler.length, taslar.length); i++) { if (isimler[i]) ogeler.push({ isim: isimler[i] }); if (i < taslar.length) ogeler.push({ tas: taslar[i] }); }
      if (!ogeler.length) return s;
      const { cx, cy, r1, r2 } = uc, rm = (r1 + r2) / 2, bant = r2 - r1, m = ogeler.length, dilim = (2 * Math.PI) / m;
      const aci = (k) => -Math.PI / 2 + (k + 0.5) * dilim;
      if (isimler.length && window.GravurMotor) {
        await document.fonts?.load('48px "PZ f7"', isimler.join('')).catch(() => {});
        const n = 1000, k = n / (2 * r2), R = rm * k, E = tuval(n, n), kc = E.getContext('2d', { willReadFrequently: true });
        kc.fillStyle = '#000'; kc.textAlign = 'center'; kc.textBaseline = 'middle';
        ogeler.forEach((o, i) => {
          if (!o.isim) return;
          const t = o.isim.toUpperCase(), th = aci(i), ust = Math.sin(th) < 0.05;
          let boy = bant * 0.46 * k; const font = () => { kc.font = `${boy}px "PZ f7", Arial, sans-serif`; };
          font();
          const ara = () => boy * 0.1, gen = () => [...t].reduce((w, ch) => w + kc.measureText(ch).width + ara(), -ara());
          const azami = dilim * 0.82 * R;
          if (gen() > azami) { boy *= azami / gen(); font(); }
          let s0 = ust ? -gen() / 2 : gen() / 2;
          for (const ch of t) {
            const w = kc.measureText(ch).width, orta = ust ? s0 + w / 2 : s0 - w / 2, phi = th + orta / R;
            kc.save(); kc.translate(n / 2 + R * Math.cos(phi), n / 2 + R * Math.sin(phi)); kc.rotate(ust ? phi + Math.PI / 2 : phi - Math.PI / 2);
            kc.fillText(ch, 0, boy * 0.04); kc.restore();
            s0 += ust ? w + ara() : -(w + ara());
          }
        });
        const d = kc.getImageData(0, 0, n, n).data, M = new Float32Array(n * n);
        for (let i = 0; i < M.length; i++) M[i] = d[i * 4 + 3] / 255;
        M.boyut = n;
        const img = c.getImageData(0, 0, s.w, s.h);
        window.GravurMotor.isle(img, { cx, cy, r: r2 }, M, metal);
        c.putImageData(img, 0, 0);
      }
      for (const [i, o] of ogeler.entries()) if (o.tas !== undefined) await this.tasKoy(c, [cx + rm * Math.cos(aci(i)), cy + rm * Math.sin(aci(i))], uc.tasCap, o.tas, metal);
      return s;
    }

    // A birth flower bouquet (Enos 23/24/36): a tight bunch of round flower heads (our birth flower links without their
    // rings), thin straight stems that meet at one point under the bunch, a small bow there and the stem ends fanning out
    // below it; the top flower carries the jump ring the bouquet hangs from. BUKET: where each head sits (mm from the
    // meeting point of the stems), the first chosen flower at the top.
    async buketSprite(uc, p, metal) {
      const PX = this.PXMM, B = uc.buket, F = B.fiyonk, a = uc.fields.find((x) => x.type === 'birthflowers'), sec = p.deger[a.key] || [];
      let v = sec.filter(Boolean).slice(0, a.max);
      const soluk = !v.length && !String(p.uid).startsWith('k-');     // nothing chosen yet: the sample bouquet, faded
      if (!v.length) v = (uc.ornekDeger?.flowers || ['jun', 'jan', 'oct']).slice(0, Math.max(1, Math.min(3, sec.length || 3)));
      const D = BUKET[v.length], halka = this.veri.ring, ton = TON[metal] || TON.gold;
      const [resimler, fiyonk, him] = await Promise.all([Promise.all(v.map((ay) => this.gorsel(`pz-c-bouquet-${ay}-${metal}.webp`))),
        this.gorsel(`pz-c-bouquet-bow-${metal}.webp`), this.gorsel(`pz-c-ring-${metal}.webp`)]);
      const S = Math.round(44 * PX), T = [S / 2, S * 0.72];
      const cv = tuval(S, S), c = cv.getContext('2d'); c.imageSmoothingQuality = 'high';
      if (soluk) c.globalAlpha = 0.4;
      const sap = (x0, y0, x1, y1) => {                                // a thin round wire: dark edge, face, a line of light
        const n = Math.hypot(x1 - x0, y1 - y0) || 1, ox = (-(y1 - y0) / n) * 0.05 * PX, oy = ((x1 - x0) / n) * 0.05 * PX;
        for (const [g, renk, d] of [[0.34, ton.golge, 0], [0.26, ton.ust, 0], [0.09, ton.isik, -1]]) {
          c.beginPath(); c.moveTo(x0 + ox * d, y0 + oy * d); c.lineTo(x1 + ox * d, y1 + oy * d);
          c.lineWidth = g * PX; c.lineCap = 'round'; c.strokeStyle = rgba(renk); c.stroke();
        }
      };
      const bas = v.map((ay, i) => ({ m: B.cicekler[ay], x: T[0] + D[i][0] * PX, y: T[1] + D[i][1] * PX }));
      for (const b of bas) sap(b.x, b.y + b.m.h * 0.2, T[0], T[1]);
      for (const dx of [-1.5, -0.75, 0, 0.75, 1.5]) sap(T[0], T[1] + 0.4 * PX, T[0] + dx * PX, T[1] + 3.6 * PX);
      const tepe = bas[0], binme = halka.h - halka.bag.delik[3] + 3;
      const hx = tepe.x - halka.bag.x, hy = tepe.y - tepe.m.h / 2 + 2 - (halka.h - binme);
      c.drawImage(him, hx, hy, halka.w, halka.h);                     // the ring's lower wire goes behind the top flower
      const sira = bas.map((b, i) => i).sort((i, j) => bas[j].y - bas[i].y);      // lower heads first, the top one last
      for (const i of sira) { const b = bas[i]; c.drawImage(resimler[i], b.x - b.m.w / 2, b.y - b.m.h / 2, b.m.w, b.m.h); }
      c.drawImage(fiyonk, T[0] - F.dugum[0], T[1] - F.dugum[1], F.w, F.h);
      // trim to what was drawn (read on a quarter-size copy)
      const q = 4, n = Math.ceil(S / q), kc = tuval(n, n), kx = kc.getContext('2d', { willReadFrequently: true }); kx.drawImage(cv, 0, 0, n, n);
      const al = kx.getImageData(0, 0, n, n).data; let x0 = n, y0 = n, x1 = 0, y1 = 0;
      for (let y = 0; y < n; y++) for (let x = 0; x < n; x++) if (al[(y * n + x) * 4 + 3] > 2) { if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; }
      x0 = Math.max(0, x0 * q - q); y0 = Math.max(0, y0 * q - q); x1 = Math.min(S, x1 * q + 2 * q); y1 = Math.min(S, y1 * q + 2 * q);
      const W = x1 - x0, H = y1 - y0, sonuc = tuval(W, H); sonuc.getContext('2d').drawImage(cv, -x0, -y0);
      const b = halka.bag, dx = hx - x0, dy = hy - y0;
      return { cv: sonuc, w: W, h: H, bag: { ...b, x: b.x + dx, y0: b.y0 + dy, delik: [b.delik[0] + dx, b.delik[1] + dy, b.delik[2] + dx, b.delik[3] + dy], dis: [b.dis[0] + dx, b.dis[1] + dx] } };
    }

    // A raised metal letter set on the charm (the mother-of-pearl clover): cut like our letters, standing a little off
    // the inlay, so it throws a soft shadow down and to the right.
    async harfBindir(c, uc, harf, metal) {
      const h = uc.harf, font = h.font || 's1';
      await document.fonts?.load(`48px "PZ ${font}"`, harf).catch(() => {});
      const olc = tuval(4, 4).getContext('2d'); olc.font = `100px "PZ ${font}", serif`;
      const px = (h.boy * 100) / (olc.measureText('H').actualBoundingBoxAscent || 72);
      const y = this.metalYazi(harf, font, px, metal, false);
      const x = h.cx - y.w / 2, ust = h.cy - (y.y0 - y.ust / 2);
      c.save();
      c.shadowColor = 'rgba(40, 28, 16, .38)'; c.shadowBlur = Math.max(1.5, px * 0.025); c.shadowOffsetX = px * 0.012; c.shadowOffsetY = px * 0.02;
      c.drawImage(y.cv, x, ust);
      c.restore();
    }

    gravurHarita(p, S) {
      const M = window.GravurMotor, a = p.gravur.ayar;
      const { L, D } = M.yerlestir(p.gravur.gorsel, S, a);
      const E = a.stil === 'foto' ? M.foto(L, S, a.detay) : a.stil === 'damga' ? M.damga(D, S, a.detay) : M.cizgi(L, S, a.detay);
      return a.ters ? M.ters(E) : E;
    }

    gorselKazi(c, uc, p, metal) {
      const g = uc.gravur, img = c.getImageData(0, 0, uc.w, uc.h);
      window.GravurMotor.isle(img, { cx: g.cx, cy: g.cy, r: g.r }, this.gravurHarita(p, p.gravur.hizli ? 260 : 460), metal);
      c.putImageData(img, 0, 0);
    }

    // Text cut out of metal, shaded like our flat cut-out parts: the same even face and the same narrow edge, bright
    // where it faces the light and dark where it faces away.
    // bitisik = the characters overlap a little, so a number is cut as one piece. Returns the canvas and where the
    // ink sits.
    metalYazi(metin, fontId, px, metal, bitisik, sekme = 0, egik = 0, merdiven = 0) {
      const P = TON[metal] || TON.gold, aile = `"PZ ${fontId}", cursive`, PX = this.PXMM;
      const olc = tuval(4, 4).getContext('2d'); olc.font = `${px}px ${aile}`;
      const tum = olc.measureText(metin), solTasma = Math.ceil(tum.actualBoundingBoxLeft || 0);
      const ust = Math.ceil(tum.actualBoundingBoxAscent || px * 0.8), alt = Math.ceil(tum.actualBoundingBoxDescent || px * 0.25);
      const pad = Math.ceil(px * 0.12), kalin = Math.max(1.5, px * 0.032);      // thin scripts are cut with a stroke the workshop can make
      const aralik = Math.round(PX * 0.3);          // the hairline kept between two straight stems
      // merdiven: every next character a step higher (angel numbers climb like a stair, Enos 9); step = merdiven x height
      const basamak = Math.round(merdiven * ust), yukari = basamak * Math.max(0, [...metin].length - 1);
      const w = solTasma + Math.ceil(Math.max(tum.width, tum.actualBoundingBoxRight || 0)) + pad * 2 + 4 + [...metin].length * aralik + Math.ceil(egik * ust), h = ust + alt + pad * 2 + yukari;
      const x0 = pad + solTasma, y0 = pad + ust + yukari;
      const kalem = (ctx) => { ctx.font = `${px}px ${aile}`; ctx.lineJoin = 'round'; ctx.lineWidth = kalin; ctx.fillStyle = ctx.strokeStyle = '#000'; };
      const cv = tuval(w, h), c = cv.getContext('2d', { willReadFrequently: true }); kalem(c);

      // A cut-out is ONE piece of metal. The characters are set one by one. Each first gets its own floating parts
      // tied on (the dot of an i, an accent), then moves left until it touches what is already there: script letters
      // join by themselves, round letters and digits meet at a point. Two straight stems would melt into one block,
      // so they keep a hairline between them and are tied at the foot instead.
      const tek = tuval(w, h), tc = tek.getContext('2d', { willReadFrequently: true }); kalem(tc);
      const dolu = new Uint8Array(w * h), bar = Math.max(3, PX * 0.34), yeter = Math.max(8, Math.round(PX * 0.5)), enCok = Math.round(px * (bitisik ? 0.4 : 0.3));
      let kayma = 0, yeniKelime = true, onek = '';
      // Arabic and Hebrew are set as one run, never letter by letter: Arabic letters join and change shape with their
      // neighbours, and both read right to left
      const butun = /[\u0590-\u08FF\uFB1D-\uFDFF\uFE70-\uFEFF]/.test(metin);
      if (butun) { c.strokeText(metin, x0, y0); c.fillText(metin, x0, y0); const g0 = c.getImageData(0, 0, w, h).data; for (let i = 0; i < w * h; i++) dolu[i] = g0[i * 4 + 3] > 127 ? 1 : 0; }
      for (const ch of butun ? '' : metin) {
        const x = x0 + olc.measureText(onek).width - kayma, yc = y0 - basamak * [...onek].length; onek += ch;
        if (ch === ' ') { yeniKelime = true; continue; }
        const bx = Math.max(0, Math.floor(x - px * 0.35)), bw = Math.min(w - bx, Math.ceil(olc.measureText(ch).width + px * 0.7));
        tc.clearRect(0, 0, w, h);
        if (egik) tc.setTransform(1, 0, -egik, 1, egik * y0, 0);       // sporty numbers lean forward a little
        tc.strokeText(ch, x, yc); tc.fillText(ch, x, yc);
        tc.setTransform(1, 0, 0, 1, 0, 0);
        const maske = () => { const g = tc.getImageData(bx, 0, bw, h).data, m = new Uint8Array(bw * h); for (let i = 0; i < m.length; i++) m[i] = g[i * 4 + 3] > 127 ? 1 : 0; return m; };
        let m = maske();
        if (this.tekParca(tc, m, bw, h, bar, bx)) m = maske();
        let it = 0;
        if (!yeniKelime) {
          let ortak = 0;
          for (let i = 0; i < m.length && ortak < yeter; i++) if (m[i] && dolu[((i / bw) | 0) * w + bx + (i % bw)]) ortak++;
          if (ortak < yeter) {
            // per row: how far the character's left edge is from the ink on its left
            const bosluk = [];
            for (let yy = 0; yy < h; yy++) {
              let ilk = -1; for (let xx = 0; xx < bw; xx++) if (m[yy * bw + xx]) { ilk = xx; break; }
              if (ilk < 0) continue;
              const X = bx + ilk, o = yy * w; let g = 0;
              while (g <= enCok + 1 && X - g >= 0 && !dolu[o + X - g]) g++;
              if (g <= enCok + 1 && X - g >= 0) bosluk.push(g);
            }
            let bulundu = false, satir = 0;
            for (it = 0; it <= enCok && !bulundu; it++) {
              let toplam = 0; satir = 0;
              for (const g of bosluk) if (it >= g) { toplam += it - g + 1; satir++; }
              if (toplam >= yeter) { bulundu = true; break; }
            }
            if (!bulundu) it = 0;                                    // nothing within reach: it is tied on below
            else if (satir > ust * 0.3) it -= aralik;                // stem against stem
          }
        }
        for (let i = 0; i < m.length; i++) if (m[i]) { const X = bx + (i % bw) - it; if (X >= 0 && X < w) dolu[((i / bw) | 0) * w + X] = 1; }
        c.drawImage(tek, -it, 0);
        kayma += it; yeniKelime = false;
      }
      // Whatever still stands alone (a letter kept apart, a second word) is tied to the piece by a short bar.
      this.tekParca(c, dolu, w, h, bar);
      if (sekme && [...metin].length > 1) {
        // A number carries its ring in the middle. Where two digits meet their tops leave a dip: a small tab fills it
        // up to the top of the digits, so the ring sits on the piece and the chain runs clear above it.
        const g = c.getImageData(0, 0, w, h).data, ustu = (x) => { for (let y = 0; y < h; y++) if (g[(y * w + x) * 4 + 3] > 127) return y; return -1; };
        let l = w, r = 0, tepe = h;
        for (let x = 0; x < w; x++) { const t = ustu(x); if (t < 0) continue; if (x < l) l = x; if (x > r) r = x; if (t < tepe) tepe = t; }
        const cx = Math.round((l + r) / 2), yari = Math.ceil(sekme / 2); let dip = 0;
        for (let x = cx - yari; x <= cx + yari; x++) { const t = ustu(x); dip = Math.max(dip, t < 0 ? h : t); }
        if (dip - tepe > 2) {
          const alt2 = Math.min(dip + 3, tepe + Math.round(ust * 0.3)), kose = Math.min(3, yari);
          c.beginPath();
          c.moveTo(cx - yari, alt2); c.lineTo(cx - yari, tepe + kose); c.quadraticCurveTo(cx - yari, tepe, cx - yari + kose, tepe);
          c.lineTo(cx + yari - kose, tepe); c.quadraticCurveTo(cx + yari, tepe, cx + yari, tepe + kose); c.lineTo(cx + yari, alt2);
          c.closePath(); c.fill();
        }
      }

      // The ink, softened: how deep a pixel lies inside tells how much of the edge it is, and the slope tells which
      // way that edge faces (a blurred mask gives smooth edges; a pixel-stepped distance gives a saw-tooth).
      const img = c.getImageData(0, 0, w, h), d = img.data, n = w * h;
      let v = new Float32Array(n), g = new Float32Array(n);
      for (let i = 0; i < n; i++) v[i] = d[i * 4 + 3] / 255;
      const R = Math.max(1, Math.round(0.07 * PX));                  // the edge is about a tenth of a millimetre wide
      for (let tur = 0; tur < 2; tur++) {
        for (let y = 0; y < h; y++) { let t = 0; const o = y * w; for (let x = -R; x < w + R; x++) { if (x + R < w) t += v[o + x + R]; if (x - R - 1 >= 0) t -= v[o + x - R - 1]; if (x >= 0 && x < w) g[o + x] = t / (2 * R + 1); } }
        for (let x = 0; x < w; x++) { let t = 0; for (let y = -R; y < h + R; y++) { if (y + R < h) t += g[(y + R) * w + x]; if (y - R - 1 >= 0) t -= g[(y - R - 1) * w + x]; if (y >= 0 && y < h) v[y * w + x] = t / (2 * R + 1); } }
      }
      for (let y = 1; y < h - 1; y++) {
        const t = sinir((y - pad) / Math.max(1, h - 2 * pad), 0, 1);
        for (let x = 1; x < w - 1; x++) {
          const i = y * w + x; if (!d[i * 4 + 3]) continue;
          const derin = sinir((v[i] - 0.52) / 0.44, 0, 1), karisim = 1 - derin * derin * (3 - 2 * derin);      // 1 at the edge, 0 on the face
          let isik = 0;
          if (karisim > 0.004) {
            const gx = v[i + 1] - v[i - 1], gy = v[i + w] - v[i - w], boy = Math.hypot(gx, gy);
            if (boy > 1e-4) isik = (0.6 * gx + 0.8 * gy) / boy;       // light from the upper left
          }
          const sup = Math.exp(-(((x / w) * 0.55 + t * 0.45 - 0.36) ** 2) / 0.02) * 4;      // a soft bright sweep across the face
          for (let k = 0; k < 3; k++) {
            const yuz = P.ust[k] + (P.alt[k] - P.ust[k]) * t + sup;
            const yon = isik >= 0 ? P.yan[k] + (P.isik[k] - P.yan[k]) * Math.pow(isik, 0.7) : P.yan[k] + (P.golge[k] - P.yan[k]) * Math.pow(-isik, 0.7);
            d[i * 4 + k] = yuz + (yon - yuz) * karisim;
          }
        }
      }
      c.putImageData(img, 0, 0);
      // the letters moved left as they were joined: the canvas ends where the ink ends
      let inkSol = w, inkSag = 0;
      for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) if (d[(y * w + x) * 4 + 3] > 8) { if (x < inkSol) inkSol = x; if (x > inkSag) inkSag = x; }
      if (inkSag < inkSol) { inkSol = 0; inkSag = w - 1; }
      const kirp = tuval(inkSag - inkSol + 1 + pad * 2, h);
      kirp.getContext('2d').drawImage(cv, pad - inkSol, 0);
      return { cv: kirp, w: kirp.width, h, y0, ust, alt, pad };
    }

    // Ties the floating parts of a cut-out to its largest part with short straight bars, the nearest part first.
    // dolu = the ink as a w × h mask; the bars are drawn on c, ox pixels to the right. Returns how many were needed.
    tekParca(c, dolu, w, h, kalinlik, ox = 0) {
      const etiket = new Int32Array(w * h), parcalar = [];
      for (let i = 0; i < dolu.length; i++) {
        if (!dolu[i] || etiket[i]) continue;
        const no = parcalar.length + 1, yigin = [i], kenar = []; let adet = 0; etiket[i] = no;
        while (yigin.length) {
          const j = yigin.pop(), x = j % w; adet++;
          let kenarda = x === 0 || x === w - 1 || j < w || j >= dolu.length - w;
          for (const k of [j - 1, j + 1, j - w, j + w]) {
            if (k < 0 || k >= dolu.length || (k === j - 1 && x === 0) || (k === j + 1 && x === w - 1)) continue;
            if (!dolu[k]) kenarda = true;
            else if (!etiket[k]) { etiket[k] = no; yigin.push(k); }
          }
          if (kenarda) kenar.push(j);
        }
        if (adet >= 8) parcalar.push({ adet, kenar });          // a few stray pixels are not a part
      }
      if (parcalar.length < 2) return 0;
      parcalar.sort((a, b) => b.adet - a.adet);
      const seyrek = (l, n) => { const adim = Math.max(1, Math.ceil(l.length / n)); return l.filter((_, i) => i % adim === 0); };
      let ana = seyrek(parcalar[0].kenar, 2400); const kalan = parcalar.slice(1).map((p) => seyrek(p.kenar, 320)), adet = kalan.length;
      c.save(); c.lineCap = 'round'; c.lineWidth = kalinlik;
      while (kalan.length) {
        let enUz = Infinity, sira = 0;
        kalan.forEach((kenar, n) => {
          for (const a of kenar) { const ax = a % w, ay = (a / w) | 0; for (const b of ana) { const dx = (b % w) - ax, dy = ((b / w) | 0) - ay, uz = dx * dx + dy * dy; if (uz < enUz) { enUz = uz; sira = n; } } }
        });
        // every pair of points at about that distance: two stems side by side are tied at the foot, a dot above its
        // stem in the middle
        const esik = (Math.sqrt(enUz) + 1.5) ** 2, esit = [];
        for (const a of kalan[sira]) { const ax = a % w, ay = (a / w) | 0; for (const b of ana) { const bx = b % w, by = (b / w) | 0; if ((bx - ax) ** 2 + (by - ay) ** 2 <= esik) esit.push({ ax, ay, bx, by }); } }
        let altY = -Infinity, ustY = Infinity;
        for (const e of esit) { altY = Math.max(altY, e.ay + e.by); ustY = Math.min(ustY, e.ay + e.by); }
        const alttakiler = esit.filter((e) => e.ay + e.by >= altY - 2).sort((p, q) => p.ax - q.ax), en = { ...alttakiler[alttakiler.length >> 1] };
        if (altY - ustY > kalinlik * 2) { en.ay -= kalinlik * 0.6; en.by -= kalinlik * 0.6; }      // the bar ends level with the feet
        const L = Math.hypot(en.bx - en.ax, en.by - en.ay) || 1, ux = (en.bx - en.ax) / L, uy = (en.by - en.ay) / L, ic = kalinlik * 0.9;
        c.beginPath(); c.moveTo(ox + en.ax - ux * ic, en.ay - uy * ic); c.lineTo(ox + en.bx + ux * ic, en.by + uy * ic); c.stroke();
        ana = ana.concat(kalan.splice(sira, 1)[0]);
      }
      c.restore();
      return adet;
    }

    async yaziSprite(uc, p, metal) {
      const t = uc.text, alan = uc.fields[0];
      const fontId = t.font || p.deger.font || t.defaultFont || this.veri.fonts[0].id;
      let girilen = String(p.deger[alan.key] || '').trim();
      // an Arabic or Hebrew plate: a name typed in English letters is written in that script by the workshop, so the
      // preview shows the example (faintly), never the English letters as if they were the piece
      if (t.betik && girilen && !BETIK[t.betik].test(girilen)) girilen = '';
      await document.fonts?.load(`48px "PZ ${fontId}"`, girilen || t.placeholder || t.sample || 'A').catch(() => {});
      const metin = girilen || t.placeholder || t.sample, cokRakam = Boolean(t.joined) && [...metin].length > 1;
      const ayak = Math.ceil((this.veri.ring.bag.delik[2] - this.veri.ring.bag.delik[0]) / 2) + 2;        // half of the ring's foot
      // the size is given as the height of a capital letter (a digit on numbers), so every font cuts the same height
      const olc = tuval(4, 4).getContext('2d'); olc.font = `100px "PZ ${fontId}", cursive`;
      const px = t.capMm ? (t.capMm * this.PXMM * 100) / (olc.measureText(t.joined ? '0' : 'H').actualBoundingBoxAscent || 72) : t.sizeMm * this.PXMM;
      const y = this.metalYazi(metin, fontId, px, metal, t.joined, t.kind === 'hang' && cokRakam && !t.merdiven ? ayak * 2 + 6 : 0, t.slant || 0, t.merdiven || 0);
      // nothing typed yet: the placeholder shows faintly, never as the customer's design
      const soluk = (kaynak) => { if (girilen) return kaynak; const f = tuval(kaynak.width, kaynak.height), fc = f.getContext('2d'); fc.globalAlpha = 0.4; fc.drawImage(kaynak, 0, 0); return f; };
      if (t.kind === 'plate') {
        // A name plate sits in the chain. A small ring on top of its first letter and one on top of its last take
        // the chain, so the name hangs below the chain and cannot turn over (the source's name necklaces are made
        // this way). Each ring stands on the highest point of the outer part of its letter.
        const d = y.cv.getContext('2d').getImageData(0, 0, y.w, y.h).data;
        const ust = (xx) => { for (let yy = 0; yy < y.h; yy++) if (d[(yy * y.w + xx) * 4 + 3] > 128) return yy; return -1; };
        let xl = 0, xr = y.w - 1;
        while (xl < y.w - 1 && ust(xl) < 0) xl++;
        while (xr > 0 && ust(xr) < 0) xr--;
        // the chain's end link hooks into a small ring, on a heavy chain too (Enos 148: a curb chain on two tiny rings)
        const r = Math.max(px * 0.068, 0.55 * this.PXMM), tel = r * 0.42;
        const ton = TON[metal] || TON.gold, pencere = Math.round(px * 0.2);
        const tepe = (x0, yon) => {
          let en = null;
          for (let k = Math.ceil(r * 0.5); k <= pencere; k++) { const xx = x0 + yon * k; if (xx < xl || xx > xr) break; const yy = ust(xx); if (yy >= 0 && (!en || yy < en.y - 1)) en = { x: xx, y: yy }; }
          return en || { x: x0, y: Math.max(0, ust(x0)) };
        };
        const ek = Math.ceil(r * 2), son = tuval(y.w, y.h + ek), c = son.getContext('2d');
        const halkaCiz = (n) => {
          const cx = n.x, cy = n.y + ek - r + tel * 1.3;              // the ring's lower wire sinks into the letter
          const g = c.createLinearGradient(0, cy - r, 0, cy + r);
          g.addColorStop(0, rgba(ton.isik)); g.addColorStop(0.45, rgba(ton.ust)); g.addColorStop(1, rgba(ton.golge));
          c.beginPath(); c.arc(cx, cy, r - tel / 2, 0, Math.PI * 2);
          c.lineWidth = tel + Math.max(1.2, px * 0.012); c.strokeStyle = rgba(ton.yan); c.stroke();
          c.lineWidth = tel; c.strokeStyle = g; c.stroke();
          c.beginPath(); c.arc(cx, cy, r - tel / 2, Math.PI * 1.05, Math.PI * 1.6);
          c.lineWidth = tel * 0.4; c.lineCap = 'round'; c.strokeStyle = rgba(ton.isik); c.stroke();
          return { x: cx, y: cy };
        };
        const sol = halkaCiz(tepe(xl, 1)), sag = halkaCiz(tepe(xr, -1));
        c.drawImage(y.cv, 0, ek);
        return { cv: soluk(son), w: son.width, h: son.height, plaka: true, sol, sag, tasma: [sol.x - xl, xr - sag.x], ic: r - tel };
      }
      if (t.kind === 'yan') {
        // A letter that sits IN the chain (sideways initials, the letters of a Cuban chain): the chain runs through its
        // middle. With halkali, a small ring stands at each side for the chain; without, letters touch one another.
        const d = y.cv.getContext('2d').getImageData(0, 0, y.w, y.h).data;
        let xl = y.w, xr = 0, yu = y.h, ya = 0;
        for (let yy = 0; yy < y.h; yy++) for (let xx = 0; xx < y.w; xx++) if (d[(yy * y.w + xx) * 4 + 3] > 128) { if (xx < xl) xl = xx; if (xx > xr) xr = xx; if (yy < yu) yu = yy; if (yy > ya) ya = yy; }
        const orta = (yu + ya) / 2;
        if (!t.halkali) {                                     // cut to the ink, so that letters set side by side touch
          const kc = tuval(xr - xl + 1, y.h); kc.getContext('2d').drawImage(y.cv, -xl, 0);
          return { cv: soluk(kc), w: kc.width, h: y.h, bag: { tip: 'kay', x: kc.width / 2, yc: orta, egim: 1 }, boncuk: true };
        }
        // each ring touches the outermost ink of the middle band (a script name's first and last letter), both on one line
        const ton = TON[metal] || TON.gold, r = Math.max(0.42 * this.PXMM, px * 0.075), tel = r * 0.42, ek = Math.ceil(r * 2);
        const b0 = Math.round(yu + (ya - yu) * 0.3), b1 = Math.round(yu + (ya - yu) * 0.75);
        let sl = { x: y.w, y: orta }, sg = { x: 0, y: orta };
        for (let yy = b0; yy <= b1; yy++) {
          for (let xx = 0; xx < y.w; xx++) if (d[(yy * y.w + xx) * 4 + 3] > 128) { if (xx < sl.x) sl = { x: xx, y: yy }; break; }
          for (let xx = y.w - 1; xx >= 0; xx--) if (d[(yy * y.w + xx) * 4 + 3] > 128) { if (xx > sg.x) sg = { x: xx, y: yy }; break; }
        }
        const hy = (sl.y + sg.y) / 2, hx = [sl.x + ek - r + tel * 1.5, sg.x + ek + r - tel * 1.5];
        const son = tuval(y.w + ek * 2, y.h), c = son.getContext('2d');
        for (const cx of hx) {
          const g = c.createLinearGradient(0, hy - r, 0, hy + r);
          g.addColorStop(0, rgba(ton.isik)); g.addColorStop(0.45, rgba(ton.ust)); g.addColorStop(1, rgba(ton.golge));
          c.beginPath(); c.arc(cx, hy, r - tel / 2, 0, Math.PI * 2);
          c.lineWidth = tel + Math.max(1.2, px * 0.012); c.strokeStyle = rgba(ton.yan); c.stroke();
          c.lineWidth = tel; c.strokeStyle = g; c.stroke();
        }
        c.drawImage(y.cv, ek, 0);
        // the chain ends in the two rings, it does not run behind the letters
        return { cv: soluk(son), w: son.width, h: son.height, bag: { tip: 'kay', x: (hx[0] + hx[1]) / 2, yc: hy, egim: 1 }, istasyon: true, halkalar: hx };
      }
      const cv = soluk(y.cv);
      // An initial or a number hangs from a jump ring. On a number of several digits the ring is always in the middle
      // (on the tab between the digits). On a letter or a single digit it sits where the piece can carry it and hang
      // straight: as near to the middle as the ink allows without dropping far below the top (the middle of a "U" is
      // empty, one of its stems is not; a "4" carries it on its peak).
      // tup: a tube bail instead of a jump ring (the Old English initial, Enos 125); it sits on the letter by its own ring
      const tup = t.tup && this.veri.tube, halka = tup || this.veri.ring, him = await this.gorsel(tup ? `pz-c-tube-${metal}.webp` : `pz-c-ring-${metal}.webp`);
      const d = y.cv.getContext('2d').getImageData(0, 0, y.w, y.h).data;
      const tepe = new Int16Array(y.w).fill(-1);
      for (let xx = 0; xx < y.w; xx++) for (let yy = 0; yy < y.h; yy++) if (d[(yy * y.w + xx) * 4 + 3] > 200) { tepe[xx] = yy; break; }
      let inkSol = 0, inkSag = y.w - 1, enUst = y.h;
      while (inkSol < y.w - 1 && tepe[inkSol] < 0) inkSol++;
      while (inkSag > 0 && tepe[inkSag] < 0) inkSag--;
      for (let xx = inkSol; xx <= inkSag; xx++) if (tepe[xx] >= 0) enUst = Math.min(enUst, tepe[xx]);
      const merkez = (inkSol + inkSag) / 2;
      let ustY = enUst, tepeX = merkez, enIyi = Infinity;
      for (let xx = inkSol + ayak; xx <= inkSag - ayak; xx++) {
        let dip = 0;                                        // the ring needs ink under its whole foot
        for (let k = -ayak; k <= ayak && dip >= 0; k++) dip = tepe[xx + k] < 0 ? -1 : Math.max(dip, tepe[xx + k]);
        if (dip < 0) continue;
        const bedel = ((cokRakam ? 40 : 1) * Math.abs(xx - merkez)) / Math.max(1, inkSag - inkSol) + (1.3 * (dip - enUst)) / Math.max(1, y.ust + y.alt);
        if (bedel < enIyi) { enIyi = bedel; tepeX = xx; ustY = dip; }
      }
      const binme = halka.h - (halka.alt ?? halka.bag.delik[3]) + 3;     // the ring's lower wire disappears behind the letter
      const sol = Math.max(0, halka.bag.x - tepeX), ustBosluk = Math.max(0, halka.h - binme - ustY);
      const w = Math.ceil(Math.max(y.w + sol, sol + tepeX - halka.bag.x + halka.w)), h = Math.ceil(y.h + ustBosluk);
      const son = tuval(w, h), c = son.getContext('2d');
      const hx = sol + tepeX - halka.bag.x, hy = ustBosluk + ustY - (halka.h - binme);
      c.drawImage(him, hx, hy, halka.w, halka.h);
      c.drawImage(cv, sol, ustBosluk);
      const kaydir = (v, dx, dy) => ({ ...v, x: v.x + dx, y0: v.y0 + dy, ...(v.delik ? { delik: [v.delik[0] + dx, v.delik[1] + dy, v.delik[2] + dx, v.delik[3] + dy], dis: [v.dis[0] + dx, v.dis[1] + dx] } : {}) });
      return { cv: son, w, h, yazi: true, bag: kaydir(halka.bag, hx, hy) };
    }

    // ——————————————————————————————————————————————— the necklace

    // The outline a necklace takes when it is laid flat: a soft, almost round egg, a touch taller than wide and a touch
    // narrower at the bottom, so a row of charms lies on a gentle curve. Unit size; it starts at the clasp (top), runs
    // down the left side, round the bottom and up the right.
    halkaEgrisi() {
      if (this._egri) return this._egri;
      const N = 720, x = new Float64Array(N + 1), y = new Float64Array(N + 1), t = new Float64Array(N + 1);
      for (let i = 0; i <= N; i++) {
        const u = (2 * Math.PI * i) / N;
        x[i] = -Math.sin(u) * (1 - 0.045 * (1 - Math.cos(u)));
        y[i] = 1.04 * (1 - Math.cos(u));
        if (i) t[i] = t[i - 1] + Math.hypot(x[i] - x[i - 1], y[i] - y[i - 1]);
      }
      return (this._egri = { N, x, y, t, P: t[N] });
    }

    // The whole necklace, laid flat and at true size (millimetres; x = 0 is its middle, y = 0 its top). The chain is
    // a closed loop of the chosen length with the clasp at the top. The charms sit side by side round the bottom of
    // the loop, each at right angles to the chain, the way a jeweller lays a charm necklace out: a few hang almost
    // straight, a full row fans out, and nothing ever lies on top of its neighbour. A name plate is part of the
    // chain: it lies level at the bottom, and the charms before and after it sit on the chain beside its ends.
    // A charm the customer has slid along the chain stays where it was put (konum[i]: mm along the chain from the
    // middle, minus = left); tutulan = the one being dragged right now, which the others make way for.
    yerlesim(sprites, z, { konum = [], tutulan = -1 } = {}) {
      const d = this.durum, PX = this.PXMM, n = sprites.length, E = this.halkaEgrisi();
      const cizgi = (z.lineMm || z.mm) * PX;                          // thickness of the chain line in sprite pixels
      const oge = sprites.map((s) => {
        if (s.plaka) {
          // a plate is carried by a ring on its first and on its last letter; ym = the line half way between them
          const ym = (s.sol.y + s.sag.y) / 2;
          return { plaka: true, w: (s.sag.x - s.sol.x) / PX, dL: (s.sol.y - ym) / PX, dR: (s.sag.y - ym) / PX, bas: s.sol.x / PX, tamW: s.w / PX, ust: ym / PX, alt: (s.h - ym) / PX, tasma: s.tasma.map((v) => Math.max(0, v) / PX) };
        }
        const b = s.bag;
        // the point of the picture that sits on the chain: the middle of a threaded piece, the top of a ring's hole
        let ay = b.tip === 'kay' ? b.yc : b.y0 + cizgi / 2;
        if (b.tip === 'halka') ay = cizgi >= b.delik[3] - b.delik[1] ? (b.delik[1] + b.delik[3]) / 2 : b.y0 + cizgi / 2 + 1;
        const o = { w: s.w / PX, h: s.h / PX, px: b.x / PX, py: ay / PX, yazi: Boolean(s.yazi), boncuk: Boolean(s.boncuk), istasyon: Boolean(s.istasyon), kay: b.tip === 'kay',
          aralik: s.aralik || (s.istasyon ? 22 : 0), tekAralik: s.tekAralik || 0, bitisik: Boolean(s.bitisik), yelpaze: s.yelpaze || 0 };
        // a stone of a row (a ring at each side): soldered to its neighbour in the row, whose body takes its ring's place
        if (s.boncuk && s.halkalar) { o.sira = true; o.kesL = Math.max(0, 2 * s.halkalar[0] - 2) / PX; o.kesR = Math.max(0, 2 * (s.w - s.halkalar[1]) - 2) / PX; }
        return o;
      });
      // The least room between two neighbours (a left of b): bodies nearly touch; cut-out letters get a little air; letters
      // threaded like beads touch; the stones of a row are soldered (their rings give way to the neighbour); the letters of
      // a Cuban chain join into one piece. araOto = where a piece goes while the customer has not moved it: spaced letters
      // and the pieces set in the chain (stations) keep their own distance (aralik, mm; Enos 2, 1, 12, 71, 55 ...).
      const ara = (a, b) => (a.sira && b.sira ? 0.05 - a.kesR - b.kesL : a.boncuk && b.boncuk ? (a.bitisik && b.bitisik ? -0.25 : 0.05) : a.istasyon || b.istasyon ? 3 : a.yazi || b.yazi ? 0.9 : 0.4);
      // tekAralik: a piece that keeps its distance from any neighbour (a sideways name sits ~3 cm beside the middle, Enos 57)
      const araOto = (a, b) => Math.max(a.aralik && b.aralik ? Math.max(ara(a, b), a.aralik, b.aralik) : ara(a, b), a.tekAralik, b.tekAralik);
      const L = d.boy * 25.4, toka = this.veri.clasp, tokaMm = toka ? toka.mm : 0;

      // from the first name plate to the last (with what hangs between them): one level piece across the bottom
      const ilkP = oge.findIndex((o) => o.plaka), sonP = ilkP < 0 ? -1 : n - 1 - [...oge].reverse().findIndex((o) => o.plaka);
      let grup = null;
      if (ilkP >= 0) {
        const Rm = 110, parca = []; let aci = 0, boy = 0;             // it sags a little: a wide circle of radius Rm
        for (let i = ilkP; i <= sonP; i++) {
          const o = oge[i];
          if (i > ilkP) {                                             // clear of the letters that reach past their rings
            const once = oge[i - 1], g = 1.2 + (once.plaka ? once.tasma[1] : 0) + (o.plaka ? o.tasma[0] : 0);
            aci += g / Rm; boy += g;
          }
          if (o.plaka) { const da = 2 * Math.asin(Math.min(1, o.w / (2 * Rm))); parca.push({ i, a0: aci, a1: aci + da }); aci += da; }
          else { parca.push({ i, a0: aci + o.px / Rm }); aci += o.w / Rm; }
          boy += o.w;
        }
        grup = { Rm, parca, yarim: aci / 2, e: Rm * Math.sin(aci / 2), cukur: Rm * (1 - Math.cos(aci / 2)), boy };
      }

      // the egg at the size that makes the whole loop exactly L long (a = the unit arc the plates take the place of)
      const nokta = (tt) => {
        let lo = 0, hi = E.N;
        while (hi - lo > 1) { const m = (lo + hi) >> 1; if (E.t[m] <= tt) lo = m; else hi = m; }
        const f = (tt - E.t[lo]) / (E.t[hi] - E.t[lo] || 1);
        return { x: E.x[lo] + (E.x[hi] - E.x[lo]) * f, y: E.y[lo] + (E.y[hi] - E.y[lo]) * f, i: lo };
      };
      let S = (L - (grup ? grup.boy : 0)) / E.P, a = 0;
      if (grup) for (let k = 0; k < 6; k++) {
        let lo = 0, hi = E.P * 0.5;
        for (let q = 0; q < 32; q++) { const m = (lo + hi) / 2; if (nokta(E.P / 2 + m / 2).x * S < grup.e) lo = m; else hi = m; }
        a = lo; S = (L - grup.boy) / (E.P - a);
      }
      const egri = (ta, tb) => {                                      // the egg from ta to tb, both ends exact
        const p0 = nokta(ta), p1 = nokta(tb), dizi = [{ x: p0.x * S, y: p0.y * S }];
        for (let i = p0.i + 1; i <= p1.i; i++) if (E.t[i] > ta + 1e-9 && E.t[i] < tb - 1e-9) dizi.push({ x: E.x[i] * S, y: E.y[i] * S });
        dizi.push({ x: p1.x * S, y: p1.y * S });
        return dizi;
      };
      const bukul = (dizi, bastan, hedef) => {                        // the chain's last 16 mm bend to meet a plate's ring
        const m = dizi.length, uc = dizi[bastan ? 0 : m - 1], dx = hedef.x - uc.x, dy = hedef.y - uc.y;
        let dist = 0, onceki = { x: uc.x, y: uc.y };
        for (let k = 0; k < m; k++) {
          const p = dizi[bastan ? k : m - 1 - k];
          dist += Math.hypot(p.x - onceki.x, p.y - onceki.y); onceki = { x: p.x, y: p.y };
          if (dist >= 16) break;
          const w = 1 - dist / 16, g = w * w * (3 - 2 * w);
          p.x += dx * g; p.y += dy * g;
        }
      };
      const t0 = tokaMm / 2 / S, solYol = egri(t0, E.P / 2 - a / 2), sagYol = egri(E.P / 2 + a / 2, E.P - t0);
      const orta = [];                                                // the plates' own points: ring to ring
      if (grup) {
        const yc = solYol[solYol.length - 1].y, cm = yc + grup.cukur - grup.Rm;
        const gn = (aq) => ({ x: grup.Rm * Math.cos(Math.PI / 2 + grup.yarim - aq), y: cm + grup.Rm * Math.sin(Math.PI / 2 + grup.yarim - aq) });
        for (const q of grup.parca) {
          const o = oge[q.i];
          if (q.a1 === undefined) { orta.push({ q, p: gn(q.a0) }); continue; }
          const P0 = gn(q.a0), P1 = gn(q.a1), ac = Math.atan2(P1.y - P0.y, P1.x - P0.x), nx = -Math.sin(ac), ny = Math.cos(ac);
          q.P0 = P0; q.aci = ac;
          orta.push({ q, bas: true, p: { x: P0.x + nx * o.dL, y: P0.y + ny * o.dL } }, { q, son: true, p: { x: P1.x + nx * o.dR, y: P1.y + ny * o.dR } });
        }
        bukul(solYol, false, orta[0].p); bukul(sagYol, true, orta[orta.length - 1].p);
      }
      const yol = [];                                                 // the chain as a line of points, clasp to clasp
      const ekle = (p) => { const s = yol[yol.length - 1]; if (!s || Math.hypot(p.x - s.x, p.y - s.y) > 1e-3) yol.push(p); return yol.length - 1; };
      solYol.forEach(ekle);
      const solIdx = yol.length - 1;
      for (const g of orta) { const j = ekle(g.p); if (g.bas) g.q.j0 = j; else if (g.son) g.q.j1 = j; else g.q.j = j; }
      const sagIdx = yol.length - 1;
      sagYol.forEach(ekle);
      for (let i = 0, s = 0; i < yol.length; i++) { if (i) s += Math.hypot(yol[i].x - yol[i - 1].x, yol[i].y - yol[i - 1].y); yol[i].s = s; }
      const sSon = yol[yol.length - 1].s;
      const bul = (s) => {                                            // point and direction of the chain at arc position s
        let lo = 0, hi = yol.length - 1;
        while (hi - lo > 1) { const m = (lo + hi) >> 1; if (yol[m].s <= s) lo = m; else hi = m; }
        const p = yol[lo], q = yol[hi], f = q.s > p.s ? sinir((s - p.s) / (q.s - p.s), 0, 1) : 0;
        return { x: p.x + (q.x - p.x) * f, y: p.y + (q.y - p.y) * f, s };
      };
      const yer = new Array(n);                                       // where each part meets the chain
      const as = (i, s) => {                                          // sets part i on the chain at s, at right angles to it
        s = sinir(s, 0.5, sSon - 0.5);
        const p = bul(s), p1 = bul(Math.max(0, s - 1.5)), p2 = bul(Math.min(sSon, s + 1.5));
        p.egim = Math.atan2(p2.y - p1.y, p2.x - p1.x); yer[i] = p;
      };
      let sikisik = false;
      const sAlt = (yol[solIdx].s + yol[sagIdx].s) / 2, MARJ = 12;       // the middle of the necklace; charms stay clear of the clasp
      const serbest = new Array(n).fill(false), dizi = [];               // which charms slide; all parts in order along the chain
      // Some chains take a charm only in certain places: a paperclip chain in the middle of a link (not where two links
      // hook into each other), a satellite chain between its beads. u = the place in the chain's repeating picture; the
      // picture is laid so that a repeat starts at the middle of the necklace (see kayma in necklace()).
      // A piece the chain runs through (a floating pearl, a bubble letter) cannot pass a bead or pearl fixed on the chain:
      // its whole body stays clear of it, not just the point where it sits on the chain.
      // On a chain of two halves, what the half under the charm allows (between two stones / in the middle of a link).
      const yuvala = z.yuva || z.yasak || z.yari ? (s, i) => {
        const Z = z.yari ? z.yari[s < sAlt ? 0 : 1] : z;
        if (!Z.yuva && !Z.yasak) return s;
        const W = Z.w, u = ((((s - sAlt) * PX + (Z.uj || 0)) % W) + W) % W;
        if (Z.yuva) {
          let en = Infinity;
          for (const m of Z.yuva) for (const d of [m - W, m, m + W]) if (Math.abs(d - u) < Math.abs(en)) en = d - u;
          return s + en / PX;
        }
        for (const [b, yar] of Z.yasak) for (const d of [b - W, b, b + W]) {
          const fark = (u - d) / PX, r = yar + (oge[i]?.kay ? oge[i].w / 2 - 0.35 : 0);
          if (Math.abs(fark) < r) return s + (fark < 0 ? -r : r) - fark;
        }
        return s;
      } : null;
      // Charms on one stretch of chain [lo, hi] (arc mm), in order. Each wants to be at its target: where the customer
      // put it, or its place in the row. They keep a little air between them and stay on the stretch; the closest such
      // arrangement comes from pooling neighbours that push into each other (weighted pool-adjacent-violators on the
      // targets less the room before them). The charm being dragged weighs most, one the customer placed more than one
      // in the row, so the row makes way. More than the stretch can carry: they close up.
      const yerlestir = (liste, lo, hi, hedef) => {
        const m = liste.length; if (!m) return;
        const o = liste.map((i) => oge[i]), h = liste.map(hedef);
        let c = [0];
        for (let k = 1; k < m; k++) c.push(c[k - 1] + (o[k - 1].w - o[k - 1].px) + ara(o[k - 1], o[k]) + o[k].px);
        const alt = lo + o[0].px, ust = hi - (o[m - 1].w - o[m - 1].px);
        const f = c[m - 1] > ust - alt ? Math.max(0.05, (ust - alt) / c[m - 1]) : 1;
        if (f < 0.97) sikisik = true;
        c = c.map((v) => v * f);
        const yHi = Math.max(alt, ust - c[m - 1]);
        const coz = (hh) => {
          const blok = [];
          liste.forEach((i, k) => {
            const w = i === tutulan ? 400 : konum[i] != null ? 25 : 1;
            let b = { t: w * (hh[k] - c[k]), w, n: 1 };
            while (blok.length && blok[blok.length - 1].t / blok[blok.length - 1].w > b.t / b.w) { const a = blok.pop(); b = { t: a.t + b.t, w: a.w + b.w, n: a.n + b.n }; }
            blok.push(b);
          });
          const p = []; let k = 0;
          for (const b of blok) for (let r = 0; r < b.n; r++, k++) p.push(sinir(b.t / b.w, alt, yHi) + c[k]);
          return p;
        };
        let p = coz(h);
        if (yuvala) p = coz(p.map((v, k) => yuvala(v, liste[k])));     // each one where the chain can take it, order and air kept
        p.forEach((v, k) => { as(liste[k], v); serbest[liste[k]] = true; });
      };
      const sirala = (liste, hedef) => liste.sort((a, b) => hedef(a) - hedef(b) || a - b);
      if (!grup) {
        // the row of charms the customer has not moved: side by side round the middle, in their order
        const oto = [...Array(n).keys()].filter((i) => konum[i] == null), otoYer = {};
        let x = 0;
        oto.forEach((i, k) => { if (k) x += araOto(oge[oto[k - 1]], oge[i]); otoYer[i] = x + oge[i].px; x += oge[i].w; });
        const hedef = (i) => sAlt + (konum[i] != null ? konum[i] : otoYer[i] - x / 2);
        const liste = sirala([...Array(n).keys()], hedef);
        yerlestir(liste, MARJ, sSon - MARJ, hedef);
        dizi.push(...liste);
      } else {
        for (const q of grup.parca) {
          if (q.j0 === undefined) { as(q.i, yol[q.j].s); continue; }
          const p1 = yol[q.j0], p2 = yol[q.j1];
          yer[q.i] = { plaka: true, P0: q.P0, aci: q.aci, x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2, s0: p1.s, s1: p2.s, s: (p1.s + p2.s) / 2 };
        }
        // the charms beside the plates, clear of the letters that reach past the plates' rings; those not moved sit
        // next to the plates from the ring outwards, in their order
        const sL = yol[solIdx].s - (oge[ilkP].tasma[0] + 1.6), sR = yol[sagIdx].s + (oge[sonP].tasma[1] + 1.6), otoYer = {};
        const otoSol = [...Array(ilkP).keys()].filter((i) => konum[i] == null).reverse();
        const otoSag = [...Array(n - 1 - sonP).keys()].map((k) => sonP + 1 + k).filter((i) => konum[i] == null);
        let u = 0;
        otoSol.forEach((i, k) => { if (k) u += araOto(oge[i], oge[otoSol[k - 1]]); otoYer[i] = sL - (u + oge[i].w - oge[i].px); u += oge[i].w; });
        u = 0;
        otoSag.forEach((i, k) => { if (k) u += araOto(oge[otoSag[k - 1]], oge[i]); otoYer[i] = sR + u + oge[i].px; u += oge[i].w; });
        const hedef = (i) => (konum[i] != null ? sAlt + konum[i] : otoYer[i]);
        const disarda = [...Array(n).keys()].filter((i) => i < ilkP || i > sonP);
        const solda = (i) => (konum[i] != null ? hedef(i) < sAlt : i < ilkP);   // one slid past the plates is on the other side
        const sol = sirala(disarda.filter(solda), hedef), sag = sirala(disarda.filter((i) => !solda(i)), hedef);
        yerlestir(sol, MARJ, sL, hedef);
        yerlestir(sag, sR, sSon - MARJ, hedef);
        dizi.push(...sol, ...[...Array(sonP - ilkP + 1).keys()].map((k) => ilkP + k), ...sag);
      }

      // marquises set side by side open like a fan (Enos 31): each turned a little more than its left neighbour
      for (let j = 0; j < dizi.length;) {
        let m = 1;
        while (j + m < dizi.length && oge[dizi[j]].yelpaze && oge[dizi[j + m]].yelpaze && !yer[dizi[j + m]].plaka &&
          yer[dizi[j + m]].s - oge[dizi[j + m]].px - (yer[dizi[j + m - 1]].s + oge[dizi[j + m - 1]].w - oge[dizi[j + m - 1]].px) < 1) m++;
        if (m > 1) for (let k = 0; k < m; k++) yer[dizi[j + k]].egim += ((k - (m - 1) / 2) * oge[dizi[j]].yelpaze * Math.PI) / 180;
        j += m;
      }

      // the room every part takes (for framing and for the pointer)
      const kutu = [];
      let k0 = [Infinity, Infinity, -Infinity, -Infinity];
      yer.forEach((p, i) => {
        const o = oge[i], c = Math.cos(p.plaka ? p.aci : p.egim), s = Math.sin(p.plaka ? p.aci : p.egim);
        const b = p.plaka ? [p.P0.x, p.P0.y, -o.bas, -o.ust, o.tamW - o.bas, o.alt] : [p.x, p.y, -o.px, -o.py, o.w - o.px, o.h - o.py];
        const kose = [[b[2], b[3]], [b[4], b[3]], [b[2], b[5]], [b[4], b[5]]].map(([u, v]) => [b[0] + u * c - v * s, b[1] + u * s + v * c]);
        const q = [Math.min(...kose.map((v) => v[0])), Math.min(...kose.map((v) => v[1])), Math.max(...kose.map((v) => v[0])), Math.max(...kose.map((v) => v[1]))];
        q.kose = [kose[0], kose[1], kose[3], kose[2]];              // the turned rectangle itself, corner after corner
        kutu.push(q); k0 = [Math.min(k0[0], q[0]), Math.min(k0[1], q[1]), Math.max(k0[2], q[2]), Math.max(k0[3], q[3])];
      });
      let h = [Infinity, Infinity, -Infinity, -Infinity];             // the loop itself
      for (const p of yol) h = [Math.min(h[0], p.x), Math.min(h[1], p.y), Math.max(h[2], p.x), Math.max(h[3], p.y)];
      // the frame of the whole necklace keeps room for a charm of ordinary size, so adding one does not move the picture
      const tum = [Math.min(h[0], k0[0]), Math.min(-(toka ? toka.cy / PX + 0.4 : 1), k0[1]), Math.max(h[2], k0[2]), Math.max(h[3] + 15, k0[3])];
      return { yol, yer, oge, kutu, kapsam: n ? k0 : null, tum, B: { x: 0, y: h[3] }, sAlt, sikisik, bul, serbest, sira: dizi };
    }

    // The backdrop of a picture: a pale surface, lit from the upper left (pz-builder.css paints the same on the stage).
    zeminCiz(c, w, h) {
      const g = c.createRadialGradient(w * 0.3, h * 0.22, 0, w * 0.3, h * 0.22, Math.max(w, h) * 1.05);
      g.addColorStop(0, '#f9f6f1'); g.addColorStop(0.45, ZEMIN); g.addColorStop(1, '#e4dcd1');
      c.fillStyle = g; c.fillRect(0, 0, w, h);
    }

    // What the picture shows (mm): the whole necklace, or a close-up of the charms. oran = width / height.
    gorus(Y, yakin, oran) {
      let k;
      if (!yakin) { const pay = (Y.tum[3] - Y.tum[1]) * 0.045 + 3; k = [Y.tum[0] - pay, Y.tum[1] - pay, Y.tum[2] + pay, Y.tum[3] + pay]; }
      else if (Y.kapsam) k = [Y.kapsam[0] - 8, Y.kapsam[1] - 12, Y.kapsam[2] + 8, Y.kapsam[3] + 7];
      else k = [-30, Y.B.y - 40, 30, Y.B.y + 12];
      const W = Math.max(k[2] - k[0], (k[3] - k[1]) * oran, yakin ? 44 : 0), H = W / oran;
      return { x0: (k[0] + k[2]) / 2 - W / 2, y0: (k[1] + k[3]) / 2 - H / 2, W, H };
    }

    async ciz() {
      if (!this.durum) return;
      const sira = (this._cizim = (this._cizim || 0) + 1);
      const r = this.tv.getBoundingClientRect();
      if (r.width < 10 || r.height < 10) return;
      const dpr = Math.min(3, window.devicePixelRatio || 1), bw = Math.round(r.width * dpr), bh = Math.round(r.height * dpr);
      // while a charm is dragged the picture holds still, so the necklace does not move under the finger
      const cv = await this.necklace(bw, { yukseklik: bh, dpr, yakin: this.yakin, sahnede: true, gor: this._tutulan ? this._sabitGor : null });
      if (sira !== this._cizim || !cv) return;
      if (this.tv.width !== bw || this.tv.height !== bh) { this.tv.width = bw; this.tv.height = bh; }
      const c = this.tv.getContext('2d');
      c.clearRect(0, 0, bw, bh);
      c.drawImage(cv, 0, 0);
      this.classList.add('is-hazir');
      this.alanUcResmi();
      // the cards and the cart list the charms left to right along the chain (not while typing: that keeps its focus)
      const a = document.activeElement;
      if (!this._tutulan && !(a && this.contains(a) && a.matches('input, textarea')) && this.siraEsitle()) { this.parcalariKur(); this.guncelle(); }
    }

    // Puts the charms in the order they have along the chain in the last stage drawing. True when it changed.
    siraEsitle() {
      const d = this.durum, sira = this._sira;
      if (!sira || sira.length !== d.parcalar.length) return false;
      const yeni = sira.map((uid) => d.parcalar.find((p) => p.uid === uid));
      if (yeni.some((p) => !p) || yeni.every((p, i) => p === d.parcalar[i])) return false;
      d.parcalar.splice(0, d.parcalar.length, ...yeni);
      return true;
    }

    // Draws the necklace on a canvas `bw` pixels wide (square unless yukseklik is given).
    //   zemin    paint the backdrop too (the picture for the cart); otherwise the canvas stays transparent
    //   yakin    the close-up of the charms instead of the whole necklace
    //   sahnede  this is the stage drawing: remember where the charms are, for the pointer
    //   gor      a fixed view (mm), used while a charm is dragged
    async necklace(bw, { yukseklik = bw, zemin = false, dpr = 1, yakin = false, sahnede = false, gor = null } = {}) {
      const bh = yukseklik, d = this.durum, z = this.zincirById[d.zincir], PX = this.PXMM;
      const parcalar = [...d.parcalar], metal = d.metal;      // the design as it is now: it may change while images load
      let sprites, serit, toka = null;
      try {
        sprites = await Promise.all(parcalar.map((p) => this.sprite(p, metal)));
        sprites = await Promise.all(sprites.map((s, i) => this.asil(s, this.ucById[parcalar[i].uc], z, metal)));
      } catch { return null; }
      const tutulan = parcalar.findIndex((p) => p.uid === this._tutulan);
      const L = this.yerlesim(sprites, z, { konum: parcalar.map((p) => (Number.isFinite(p.konum) ? p.konum : null)), tutulan });
      const g = gor && Math.abs(gor.W / gor.H - bw / bh) < 1e-3 ? gor : this.gorus(L, yakin, bw / bh), k = bw / g.W;      // k = canvas pixels per millimetre
      const X = (mm) => (mm - g.x0) * k, Y = (mm) => (mm - g.y0) * k, o = k / PX;
      const boy = L.yol[L.yol.length - 1].s;
      // a chain of two halves (half tennis, half paperclip): each half is drawn from its own chain picture and they meet
      // at the middle of the necklace, each cut where it ends cleanly (uj: between two stones / at the start of a link)
      const yari = z.yari || null;
      let seritler = null;
      try {
        if (yari) seritler = await Promise.all(yari.map((p) => this.serit(p, metal, boy * PX + p.w)));
        else serit = await this.serit(z, metal, boy * PX + z.w);
      } catch { return null; }
      // a chain with pearls on it (pearl station) is a plain chain near the clasp: the pearls stop short of it
      const zs = z.sade && this.zincirById[z.sade];
      let sade = null;
      if (zs) { try { sade = await this.serit(zs, metal, boy * PX + z.w); } catch { sade = null; } }
      if (this.veri.clasp) { try { toka = await this.gorsel(`pz-k-clasp-${metal}.webp`); } catch { toka = null; } }
      const dip = L.sAlt * PX, kayma = Math.ceil(dip / z.w) * z.w - dip;            // a tile boundary sits at the lowest point
      const kaymaY = yari ? yari.map((p) => ((((p.uj || 0) - dip) % p.w) + p.w) % p.w) : null;   // each half's join at the lowest point
      // a plate: the chain is not drawn across it, and it ends at the wire of the plate's ring (hooked on it), not in its hole
      // a piece set in the chain by a ring at each side (a sideways cross, a station): the chain ends in those rings too
      const bosluklar = L.yer.map((y, i) => {
        const s = sprites[i];
        if (y.plaka) return [y.s0 - (s.ic || 0) / PX - 0.01, y.s1 + (s.ic || 0) / PX + 0.01];
        if (s.halkalar) return [y.s + (s.halkalar[0] - s.bag.x) / PX + 0.15, y.s + (s.halkalar[1] - s.bag.x) / PX - 0.15];
        // the letters of a Cuban chain are one piece with it: the chain ends inside the first and the last letter
        if (s.zincirsiz) return [y.s - s.bag.x / PX + 0.6, y.s + (s.w - s.bag.x) / PX - 0.6];
        return null;
      }).filter(Boolean).sort((a, b) => a[0] - b[0]).reduce((t, v) => {
        const son = t[t.length - 1];                              // pieces that touch: no chain between them either
        if (son && v[0] - son[1] < 1.6) son[1] = Math.max(son[1], v[1]); else t.push([...v]);
        return t;
      }, []);
      // the stones of a row are soldered side by side: a ring whose place a neighbour in the row takes is not drawn
      const komsu = parcalar.map(() => [false, false]);
      for (let j = 1; j < L.sira.length; j++) {
        const a = L.sira[j - 1], b = L.sira[j], A = L.oge[a], B = L.oge[b];
        if (!A.sira || !B.sira || L.yer[a].plaka || L.yer[b].plaka) continue;
        if (L.yer[b].s - B.px + B.kesL - (L.yer[a].s + A.w - A.px - A.kesR) < 0.4) { komsu[a][1] = true; komsu[b][0] = true; }
      }

      const katman = tuval(bw, bh), c = katman.getContext('2d');
      c.imageSmoothingQuality = 'high';
      const th = (z.h / PX) * k, thS = zs ? (zs.h / PX) * k : 0;
      // the repeat whose pearl (in its middle) lies within 11 mm of the clasp is drawn as the plain chain; repeats meet
      // half way between two pearls, where both pictures are the same plain chain
      const sadeMi = (sm) => { const t = Math.floor((sm * PX + kayma) / z.w), sp = ((t + 0.5) * z.w - kayma) / PX; return sp < 11 || sp > boy - 11; };
      // part f0..f1 of the stretch a -> b, from one chain picture (a half's stretch that crosses the middle is cut there)
      const parcaCiz = (im, zz, kay, a, b, f0, f1) => {
        const uz = b.s - a.s, t = (zz.h / PX) * k;
        c.drawImage(im, (a.s + f0 * uz) * PX + kay, 0, (f1 - f0) * uz * PX, zz.h, f0 * uz * k, -t / 2, (f1 - f0) * uz * k + 0.7, t);
      };
      const zincirCiz = (s0 = -Infinity, s1 = Infinity) => {
        const n = L.yol;
        for (let i = 0; i < n.length - 1; i++) {
          const a = n[i], b = n[i + 1], sm = (a.s + b.s) / 2, uz = b.s - a.s;
          if (uz < 1e-4 || sm < s0 || sm > s1 || bosluklar.some(([u, v]) => sm > u && sm < v)) continue;
          c.save();
          c.translate(X(a.x), Y(a.y)); c.rotate(Math.atan2(b.y - a.y, b.x - a.x));
          if (yari) {
            const f = Math.min(1, Math.max(0, (L.sAlt - a.s) / uz));
            if (f > 0) parcaCiz(seritler[0], yari[0], kaymaY[0], a, b, 0, f);
            if (f < 1) parcaCiz(seritler[1], yari[1], kaymaY[1], a, b, f, 1);
          } else if (sade && sadeMi(sm)) c.drawImage(sade, a.s * PX + kayma, 0, uz * PX, zs.h, 0, -thS / 2, uz * k + 0.7, thS);
          else c.drawImage(serit, a.s * PX + kayma, 0, uz * PX, z.h, 0, -th / 2, uz * k + 0.7, th);
          c.restore();
        }
      };
      zincirCiz();
      if (toka) {                                               // the clasp closes the chain at the top
        const t = this.veri.clasp;
        c.drawImage(toka, X(0) - (t.w / 2) * o, Y(L.yol[0].y) - t.cy * o, t.w * o, t.h * o);
      }

      const kutular = new Array(parcalar.length);
      // plates first, then the charms from the ends of the row inwards: what sits in the middle lies on top, and the
      // charm in the hand above everything
      const sirali = parcalar.map((p, i) => i).sort((a, b) => (a === tutulan) - (b === tutulan) || (L.yer[b].plaka ? 1 : 0) - (L.yer[a].plaka ? 1 : 0) || Math.abs(L.yer[b].s - L.sAlt) - Math.abs(L.yer[a].s - L.sAlt));
      for (const i of sirali) {
        const p = parcalar[i], s = sprites[i], y = L.yer[i], q = L.oge[i], kt = L.kutu[i];
        kutular[i] = { uid: p.uid, x: X(kt[0]), y: Y(kt[1]), w: (kt[2] - kt[0]) * k, h: (kt[3] - kt[1]) * k, kose: kt.kose.map(([u, v]) => [X(u), Y(v)]) };
        c.save();
        if (y.plaka) {
          c.translate(X(y.P0.x), Y(y.P0.y)); c.rotate(y.aci);
          c.drawImage(s.cv, -q.bas * k, -q.ust * k, s.w * o, s.h * o);
        } else {
          c.translate(X(y.x), Y(y.y)); c.rotate(y.egim);
          if (komsu[i][0] || komsu[i][1]) {
            const x0 = komsu[i][0] ? (q.kesL - q.px) * k : -1e5, x1 = komsu[i][1] ? (q.w - q.px - q.kesR) * k : 1e5;
            c.beginPath(); c.rect(x0, -1e5, x1 - x0, 2e5); c.clip();
          }
          c.drawImage(s.cv, -q.px * k, -q.py * k, s.w * o, s.h * o);
          if (s.bag.tip === 'halka') {
            // threaded look: the chain is behind the ring's left side and in front of its right side
            c.beginPath(); c.rect(0, -th, (s.bag.dis[1] + 2 - s.bag.x) * o, th * 2); c.clip();
            c.setTransform(1, 0, 0, 1, 0, 0);
            zincirCiz(y.s - 0.4, y.s + (s.bag.dis[1] - s.bag.x + 4) / PX + 1);
          }
        }
        c.restore();
      }
      if (sahnede) {                                            // what the pointer needs belongs to the stage drawing only
        this._kutular = kutular; this._olcek = bw; this._sikisik = L.sikisik;
        this._gor = { x0: g.x0, y0: g.y0, k }; this._gorMm = g; this._yol = L.yol; this._sAlt = L.sAlt;
        this._sira = L.sira.map((i) => parcalar[i].uid);
        this._ofs = Object.fromEntries(parcalar.map((p, i) => [p.uid, L.yer[i].s - L.sAlt]));     // where each one is: mm from the middle
        this._serbest = Object.fromEntries(parcalar.map((p, i) => [p.uid, L.serbest[i]]));
      }

      const son = tuval(bw, bh), sc = son.getContext('2d');
      sc.imageSmoothingQuality = 'high';
      if (zemin) this.zeminCiz(sc, bw, bh);
      // The necklace lies on the surface under a light from the upper left: a wide soft shadow, and a close, darker
      // one where the metal touches. Only the shadows are painted here (the layer itself is drawn off the canvas).
      const golge = (renk, bulanik, dx, dy) => {
        sc.save();
        sc.shadowColor = renk; sc.shadowBlur = bulanik * k; sc.shadowOffsetX = dx * k + bw * 2; sc.shadowOffsetY = dy * k;
        sc.drawImage(katman, -bw * 2, 0);
        sc.restore();
      };
      golge('rgba(92,66,40,.17)', 2.4, 1.0, 1.7);
      golge('rgba(92,66,40,.30)', 0.5, 0.24, 0.42);
      sc.drawImage(katman, 0, 0);
      return son;
    }

    kutuBul(e) {
      const r = this.tv.getBoundingClientRect(), o = (this._olcek || r.width) / r.width;
      const x = (e.clientX - r.left) * o, y = (e.clientY - r.top) * o;
      const icinde = (b) => {                                   // inside the charm's own turned rectangle
        if (x < b.x || x > b.x + b.w || y < b.y || y > b.y + b.h) return false;
        let isaret = 0;
        for (let i = 0; i < 4; i++) { const [x0, y0] = b.kose[i], [x1, y1] = b.kose[(i + 1) % 4], c = (x1 - x0) * (y - y0) - (y1 - y0) * (x - x0); if (Math.abs(c) < 1e-6) continue; if (isaret && Math.sign(c) !== isaret) return false; isaret = Math.sign(c); }
        return true;
      };
      return { x, kutu: [...(this._kutular || [])].reverse().find(icinde) };
    }

    // On the stage a charm is slid along the chain, round the bottom or up either side, and stays where it is let
    // go; the others make way. A tap on it opens its card. Name plates are part of the chain and stay put.
    sahneBagla() {
      const tv = this.tv; let s = null;
      const mm = (e) => {                                       // the pointer in millimetres of the necklace
        const r = tv.getBoundingClientRect(), g = this._gor; if (!g) return null;
        return { x: ((e.clientX - r.left) * (tv.width / r.width)) / g.k + g.x0, y: ((e.clientY - r.top) * (tv.height / r.height)) / g.k + g.y0 };
      };
      const zincirde = (p) => {                                 // the point of the chain nearest to p: mm from the clasp
        let en = Infinity, sonuc = null;
        for (const q of this._yol || []) { const u = (q.x - p.x) ** 2 + (q.y - p.y) ** 2; if (u < en) { en = u; sonuc = q.s; } }
        return sonuc;
      };
      const kayar = (kutu) => Boolean(kutu && this._serbest?.[kutu.uid]);
      // a finger on a charm moves the charm, not the page; anywhere else on the picture the page scrolls as usual
      tv.addEventListener('touchstart', (e) => { if (e.touches.length === 1 && kayar(this.kutuBul(e.touches[0]).kutu)) e.preventDefault(); }, { passive: false });
      tv.addEventListener('touchmove', (e) => { if (s?.kayar) e.preventDefault(); }, { passive: false });
      tv.addEventListener('pointerdown', (e) => {
        const { kutu } = this.kutuBul(e); if (!kutu) return;
        const p = mm(e), sz = p && zincirde(p);
        // how far along the chain the charm is from the point under the pointer: kept while it moves
        s = { uid: kutu.uid, x0: e.clientX, y0: e.clientY, id: e.pointerId, oynadi: false, mesgul: false, son: null, kayar: kayar(kutu), fark: sz === null ? 0 : this._sAlt + (this._ofs?.[kutu.uid] ?? 0) - sz };
        try { tv.setPointerCapture(e.pointerId); } catch { /* a pointer that is already gone */ }
      });
      tv.addEventListener('pointermove', async (e) => {
        if (!s) { const k = this.kutuBul(e).kutu; tv.style.cursor = k ? (kayar(k) ? 'grab' : 'pointer') : ''; return; }
        if (e.pointerId !== s.id || !s.kayar || (!s.oynadi && Math.hypot(e.clientX - s.x0, e.clientY - s.y0) < 6)) return;
        if (!s.oynadi) { s.oynadi = true; this._tutulan = s.uid; this._sabitGor = this._gorMm; tv.style.cursor = 'grabbing'; }
        s.son = { clientX: e.clientX, clientY: e.clientY };
        if (s.mesgul) return;
        s.mesgul = true;                                        // one drawing at a time; it always follows the latest move
        while (s && s.son) {
          const ev = s.son; s.son = null;
          const p = this.durum.parcalar.find((x) => x.uid === s.uid), q = mm(ev), sz = q && zincirde(q);
          if (!p || sz === null) break;
          p.konum = sz + s.fark - this._sAlt;
          await this.ciz();
        }
        if (s) s.mesgul = false;
      });
      const birak = (e) => {
        if (!s || e.pointerId !== s.id) return;
        const { uid, oynadi } = s; s = null; tv.style.cursor = '';
        if (e.type === 'pointerup' && !oynadi) return this.parcayaGit(uid);
        if (!oynadi) return;
        // where they lie now is where they stay: the charm let go, and the moved ones it pushed aside
        for (const p of this.durum.parcalar) if ((p.uid === uid || p.konum != null) && Number.isFinite(this._ofs?.[p.uid])) p.konum = Math.round(this._ofs[p.uid] * 10) / 10;
        this._tutulan = null; this._sabitGor = null;
        this.siraEsitle();
        this.parcalariKur(); this.guncelle();
      };
      tv.addEventListener('pointerup', birak); tv.addEventListener('pointercancel', birak);
    }

    parcayaGit(uid) {
      if (this.acik !== 'uc') this.alanAc('uc');
      const kart = this.querySelector(`[data-parca="${uid}"]`);
      if (!kart) return;
      // on a phone the preview covers the top of the window: the card goes right under it (scroll-margin-top)
      kart.scrollIntoView({ block: window.matchMedia('(max-width: 999px)').matches ? 'start' : 'center', behavior: 'smooth' });
      kart.classList.add('is-vurgu');
      setTimeout(() => kart.classList.remove('is-vurgu'), 1400);
    }

    // ——————————————————————————————————————————————— interface

    bust(inch) {
      const alt = Math.floor(inch / 2) * 2, ustu = Math.min(22, alt + 2), t = (inch - alt) / 2;
      const [k0, d0] = BUST[Math.max(14, alt)], [k1, d1] = BUST[ustu];
      const kenar = k0 + (k1 - k0) * t, dip = d0 + (d1 - d0) * t;
      return `<svg viewBox="0 0 48 44" width="40" height="37" aria-hidden="true" focusable="false"><path d="M17.5 2h13v9.5c0 2 1.6 3.4 4.2 4.4 5.2 2 8.8 5 9.8 10V44H2.5V25.9c1-5 4.6-8 9.8-10 2.6-1 4.2-2.4 4.2-4.4V2Z" fill="currentColor"/><path d="M17.2 ${kenar} Q24 ${dip * 2 - kenar} 30.8 ${kenar}" class="pz__bust-zincir" fill="none" stroke-width="1.3" stroke-linecap="round"/></svg>`;
    }

    alanlariKur() {
      const kap = this.querySelector('[data-alanlar]');
      kap.innerHTML = [['uc', 'Charms'], ['zincir', 'Chain'], ['metal', 'Material'], ['boy', 'Length']].map(([id, ad]) => `
        <button type="button" class="pz__alan" role="tab" id="pz-alan-${id}" aria-controls="pz-bolum-${id}" aria-selected="${id === this.acik}" data-is="alan" data-alan="${id}">
          <span class="pz__alan-gorsel" data-alan-gorsel="${id}"></span>
          <span class="pz__alan-ad">${ad}</span>
          <span class="pz__alan-deger" data-alan-deger="${id}"></span>
        </button>`).join('');
    }

    bolumleriKur() {
      const q = (s) => this.querySelector(s);
      q('[data-filtreler]').innerHTML = GRUPLAR.filter(([id]) => id === 'all' || this.uclar.some((u) => u.group === id)).map(([id, ad]) =>
        `<button type="button" class="pz__cip" data-is="filtre" data-filtre="${id}" aria-pressed="${id === this.filtre}">${ad}</button>`).join('');
      q('[data-uc-izgara]').innerHTML = this.uclar.map((u) => `
        <div class="pz__kart" data-uc="${u.id}" data-grup="${u.group}">
          <span class="pz__kart-gorsel"><canvas data-uc-tuval="${u.id}" role="img" aria-label="${kacis(u.name)}"></canvas></span>
          <span class="pz__kart-ad">${kacis(u.name)}</span>
          <span class="pz__kart-alt">${this.para(this.fiyat(u.sku))}</span>
          <span data-uc-dugme="${u.id}"></span>
        </div>`).join('');
      q('[data-zincir-izgara]').innerHTML = this.zincirler.map((z) => `
        <button type="button" class="pz__kart" role="radio" aria-checked="false" data-is="zincir" data-zincir="${z.id}">
          <span class="pz__kart-gorsel pz__kart-gorsel--yatay"><span class="pz__kart-zincir" data-zincir-gorsel="${z.id}" style="background-size:auto ${((z.h / this.PXMM) * 9).toFixed(1)}px"></span></span>
          <span class="pz__kart-ad">${kacis(z.name)}</span>
          <span class="pz__kart-alt">${this.para(this.fiyat(z.sku))}</span>
        </button>`).join('');
      q('[data-metal-izgara]').innerHTML = this.veri.metals.map((m) => `
        <button type="button" class="pz__kart" role="radio" aria-checked="false" data-is="metal" data-metal="${m.id}">
          <span class="pz__kart-gorsel"><img src="${this.adres(`pz-c-disc-${m.id}.webp`)}" alt="" loading="lazy" width="360" height="482"></span>
          <span class="pz__kart-ad">${kacis(m.short)}</span>
          <span class="pz__kart-alt">${kacis(m.name)}</span>
        </button>`).join('');
    }

    boylariKur() {
      const z = this.zincirById[this.durum.zincir];
      this.querySelector('[data-boy-izgara]').innerHTML = z.lengths.map((b) => `
        <button type="button" class="pz__kart" role="radio" aria-checked="${b === this.durum.boy}" data-is="boy" data-boy="${b}">
          <span class="pz__kart-gorsel pz__kart-gorsel--boy">${this.bust(b)}</span>
          <span class="pz__kart-ad">${b}″</span>
          <span class="pz__kart-alt">${Math.round(b * 2.54)} cm</span>
        </button>`).join('');
    }

    // The cards of the customer's own charms: order on the chain, personal details, remove.
    parcalariKur() {
      const liste = this.querySelector('[data-parcalar]'), d = this.durum;
      this.querySelector('[data-bos]').hidden = d.parcalar.length > 0;
      liste.innerHTML = d.parcalar.map((p, i) => {
        const uc = this.ucById[p.uc];
        return `
        <li class="pz__parca" data-parca="${p.uid}">
          <span class="pz__parca-gorsel"><canvas data-parca-tuval></canvas></span>
          <div class="pz__parca-ust">
            <span class="pz__parca-ad">${i + 1}. ${kacis(uc.name)}<span class="pz__parca-fiyat">${this.para(this.fiyat(this.ucSku(p)))}</span></span>
            <span class="pz__parca-dugmeler">
              <button type="button" class="pz__kucuk" data-is="sola" aria-label="Move left on the chain" ${i === 0 ? 'disabled' : ''}>←</button>
              <button type="button" class="pz__kucuk" data-is="saga" aria-label="Move right on the chain" ${i === d.parcalar.length - 1 ? 'disabled' : ''}>→</button>
              <button type="button" class="pz__kucuk" data-is="kaldir" aria-label="Remove ${kacis(uc.name)}">×</button>
            </span>
          </div>
          ${uc.fields.length ? `<div class="pz__parca-govde">${uc.fields.map((a) => this.alanHtml(p, uc, a)).join('')}<p class="pz__hata" data-parca-hata hidden role="alert"></p></div>` : ''}
        </li>`;
      }).join('');
      d.parcalar.forEach((p) => this.kartTazele(p));
    }

    // Re-renders a card's fields only (the other cards stay as they are); focus goes back to the control that was used.
    govdeYenile(p, odak) {
      const govde = this.querySelector(`[data-parca="${p.uid}"] .pz__parca-govde`); if (!govde) return;
      const uc = this.ucById[p.uc];
      govde.innerHTML = `${uc.fields.map((a) => this.alanHtml(p, uc, a)).join('')}<p class="pz__hata" data-parca-hata hidden role="alert"></p>`;
      if (odak) govde.querySelector(odak)?.focus();
      if (uc.gravur) this.gravurCiz(p);
    }

    // a card's own pictures: the thumbnail and, on engravable charms, the close-up
    kartTazele(p) { this.kucukResim(p); if (this.ucById[p.uc].gravur) this.gravurCiz(p); }

    alanHtml(p, uc, a) {
      const d = p.deger;
      if (a.type === 'birthstone') return `
        <div data-alan="${a.key}"><p class="pz__etiket"><span>${kacis(a.label)}</span><output data-tas-adi>${kacis(a.key === 'stone' ? this.ayrinti(p) : this.tasAdi(d[a.key]))}</output></p>
          <div class="pz__taslar" role="radiogroup" aria-label="${kacis(a.label)}">${this.veri.stones.map((t) =>
            `<button type="button" class="pz__tas" role="radio" aria-checked="${d[a.key] === t.id}" data-is="tas" data-tas="${t.id}" title="${kacis(t.month)} – ${kacis(t.name)}" style="--pz-tas:${t.color}"><i></i>${t.month.slice(0, 3)}</button>`).join('')}</div></div>`;
      if (a.type === 'text' || a.type === 'number') return `
        <div><label class="pz__etiket" for="pz-${p.uid}-${a.key}"><span>${kacis(a.label)}</span><output data-sayac>${String(d[a.key] || '').length}/${a.max}</output></label>
          <input class="pz__girdi" id="pz-${p.uid}-${a.key}" type="text" ${a.type === 'number' ? 'inputmode="numeric" pattern="[0-9]*"' : 'autocapitalize="words"'} autocomplete="off" spellcheck="false" maxlength="${a.max}" value="${kacis(d[a.key])}" data-girdi="${a.key}" data-tur="${a.type}" placeholder="${a.ornek ? kacis(a.ornek) : a.type === 'number' ? 'e.g. 23' : /names/i.test(a.label) ? 'e.g. Emma, Ben' : /name/i.test(a.label) ? 'e.g. Emma' : ''}">
          ${uc.text?.betik ? `<p class="pz__ipucu">${uc.text.betik === 'ar' ? 'Type the name in Arabic letters to see it on the necklace. If you type it in English, our workshop writes it in Arabic for you and the preview shows an example.' : 'Type the name in Hebrew letters to see it on the necklace. If you type it in English, our workshop writes it in Hebrew for you and the preview shows an example.'}</p>` : ''}</div>
        ${a.font ? this.fontHtml(p, uc, a) : ''}`;
      if (a.type === 'letter') return `
        <div><p class="pz__etiket"><span>${kacis(a.label)}</span></p>
          <div class="pz__harfler" role="radiogroup" aria-label="${kacis(a.label)}">${'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').map((h) =>
            `<button type="button" class="pz__harf" role="radio" aria-checked="${d[a.key] === h}" data-is="harf" data-harf="${h}">${h}</button>`).join('')}</div></div>
        ${a.font ? this.fontHtml(p, uc, a) : ''}`;
      if (a.type === 'engrave-image') return this.gravurHtml(p);
      if (a.type === 'birthflower') return `
        <div><p class="pz__etiket"><span>${kacis(a.label)}</span><output data-cicek-adi>${kacis(this.cicekAdi(d[a.key]))}</output></p>
          <div class="pz__cicekler" role="radiogroup" aria-label="${kacis(a.label)}">${(this.veri.flowers || []).map((f) =>
            `<button type="button" class="pz__cicek" role="radio" aria-checked="${d[a.key] === f.id}" data-is="cicek" data-cicek="${f.id}" title="${kacis(f.month)} – ${kacis(f.name)}"><img src="${this.adres(`pz-c-flower-stem-${f.id}-${this.durum.metal}.webp`)}" alt="" loading="lazy"><b>${kacis(f.month.slice(0, 3))}</b><small>${kacis(f.name)}</small></button>`).join('')}</div></div>`;
      if (a.type === 'birthflowers') {
        // how many flowers, then a month for each (like the family stones): the flower being chosen is highlighted
        const v = d[a.key] || [''], k = Math.min(p._aktifTas || 0, v.length - 1), en = a.min || 1, fl = this.veri.flowers || [];
        const resim = (id) => this.adres(`pz-c-flower-stem-${id}-${this.durum.metal}.webp`);
        return `
        <div data-cok-tas><p class="pz__etiket"><span>${kacis(a.label)}</span><span class="pz__adet"><button type="button" data-is="tas-azalt" aria-label="One flower less" ${v.length <= en ? 'disabled' : ''}>−</button><output aria-live="polite">${v.length}</output><button type="button" data-is="tas-artir" aria-label="One flower more" ${v.length >= a.max ? 'disabled' : ''}>+</button></span></p>
          <div class="pz__yuvalar" role="radiogroup" aria-label="Flowers">${v.map((id, i) => { const f = fl.find((x) => x.id === id);
            return `<button type="button" class="pz__yuva pz__yuva--cicek" role="radio" aria-checked="${i === k}" data-is="tas-yuva" data-yuva="${i}" ${f ? `title="${kacis(f.month)} – ${kacis(f.name)}"` : ''}><i ${f ? `style="background-image:url('${resim(f.id)}')"` : ''}></i>${i + 1}</button>`; }).join('')}</div>
          <p class="pz__ipucu">Flower ${k + 1} of ${v.length}: choose its month</p>
          <div class="pz__cicekler" role="radiogroup" aria-label="Birth flower for flower ${k + 1}">${fl.map((f) =>
            `<button type="button" class="pz__cicek" role="radio" aria-checked="${v[k] === f.id}" data-is="cicekc" data-cicek="${f.id}" title="${kacis(f.month)} – ${kacis(f.name)}"><img src="${resim(f.id)}" alt="" loading="lazy"><b>${kacis(f.month.slice(0, 3))}</b><small>${kacis(f.name)}</small></button>`).join('')}</div></div>`;
      }
      if (a.type === 'birthstones') {
        // how many stones, then a month for each: the stone being set is highlighted, the palette sets it
        const v = d[a.key] || [''], k = Math.min(p._aktifTas || 0, v.length - 1), en = a.min || 1;
        return `
        <div data-cok-tas><p class="pz__etiket"><span>${kacis(a.label)}</span><span class="pz__adet"><button type="button" data-is="tas-azalt" aria-label="One stone less" ${v.length <= en ? 'disabled' : ''}>−</button><output aria-live="polite">${v.length}</output><button type="button" data-is="tas-artir" aria-label="One stone more" ${v.length >= a.max ? 'disabled' : ''}>+</button></span></p>
          <div class="pz__yuvalar" role="radiogroup" aria-label="Stones">${v.map((id, i) => { const t = this.veri.stones.find((s) => s.id === id);
            return `<button type="button" class="pz__yuva" role="radio" aria-checked="${i === k}" data-is="tas-yuva" data-yuva="${i}" ${t ? `style="--pz-tas:${t.color}" title="${kacis(t.month)} – ${kacis(t.name)}"` : ''}><i></i>${i + 1}</button>`; }).join('')}</div>
          <p class="pz__ipucu">Stone ${k + 1} of ${v.length}: choose its month</p>
          <div class="pz__taslar" role="radiogroup" aria-label="Birthstone for stone ${k + 1}">${this.veri.stones.map((t) =>
            `<button type="button" class="pz__tas" role="radio" aria-checked="${v[k] === t.id}" data-is="tasc" data-tas="${t.id}" title="${kacis(t.month)} – ${kacis(t.name)}" style="--pz-tas:${t.color}"><i></i>${t.month.slice(0, 3)}</button>`).join('')}</div></div>`;
      }
      return '';
    }

    fontHtml(p, uc, a) {
      const ornek = String(p.deger[a.key] || '').trim() || uc.text?.sample || 'Abc';
      return `<div><p class="pz__etiket"><span>Font</span><output data-font-adi>${kacis(this.fontAdi(p.deger.font))}</output></p>
        <div class="pz__fontlar" role="radiogroup" aria-label="Font">${this.veri.fonts.map((f) =>
          `<button type="button" class="pz__font" role="radio" aria-checked="${p.deger.font === f.id}" data-is="font" data-font="${f.id}"><b style="font-family:'PZ ${f.id}',cursive" data-font-ornek>${kacis(ornek)}</b><small>No. ${f.no}</small></button>`).join('')}</div></div>`;
    }

    gravurHtml(p) {
      const a = p.gravur?.ayar, dolu = Boolean(p.gravur?.gorsel);
      return `<div class="pz__gravur ${dolu ? 'is-dolu' : ''}" data-gravur>
        <div class="pz__gravur-sahne"><canvas data-gravur-tuval width="480" height="480" tabindex="0" role="img" aria-label="Engraving preview"></canvas>
          <button type="button" class="pz__gravur-bos" data-is="gorsel-sec"><span>Upload a photo, drawing or logo</span><small>JPG or PNG · we engrave it on the charm</small></button></div>
        <input class="pz__gizli" type="file" accept="image/*" data-dosya tabindex="-1" aria-label="Upload a photo, drawing or logo">
        <div data-gravur-ayar ${dolu ? '' : 'hidden'} style="display:grid;gap:.75rem">
          <div class="pz__stiller" role="radiogroup" aria-label="Engraving style">${STILLER.map(([id, ad]) => `<button type="button" class="pz__stil" role="radio" aria-checked="${a?.stil === id}" data-is="stil" data-stil="${id}">${ad}</button>`).join('')}</div>
          <div class="pz__kaydiraclar">
            <label class="pz__kaydirac"><span>Detail</span><input type="range" min="0" max="100" value="${Math.round((a?.detay ?? 0.45) * 100)}" data-kaydirac="detay"></label>
            <label class="pz__kaydirac"><span>Size</span><input type="range" min="100" max="300" value="${Math.round((a?.olcek ?? 1) * 100)}" data-kaydirac="olcek"></label>
          </div>
          <div class="pz__satir">
            <button type="button" class="pz__metin-dugme" data-is="dondur">↻ Rotate</button>
            <button type="button" class="pz__metin-dugme" data-is="ters" aria-pressed="${Boolean(a?.ters)}">◐ Invert</button>
            <button type="button" class="pz__metin-dugme" data-is="gorsel-sec">Change image</button>
            <button type="button" class="pz__metin-dugme" data-is="gorsel-kaldir">Remove image</button>
          </div>
          <p class="pz__ipucu">Drag the picture on the charm to move it. A clear, well-lit image with a plain background engraves best.</p>
        </div>
      </div>`;
    }

    async kucukResim(p) {
      const cv = this.querySelector(`[data-parca="${p.uid}"] [data-parca-tuval]`);
      if (!cv) return;
      let s; try { s = await this.sprite(p); } catch { return; }
      const o = Math.min(96 / s.w, 96 / s.h, 1);
      cv.width = Math.round(s.w * o); cv.height = Math.round(s.h * o);
      const c = cv.getContext('2d'); c.imageSmoothingQuality = 'high';
      c.drawImage(s.cv, 0, 0, cv.width, cv.height);
    }

    // The Charms area shows the first pictured charm as it hangs on the necklace: its own stone, engraving and metal.
    // It uses the picture the necklace already made, so until that is ready the area keeps what it shows.
    alanUcResmi() {
      const kap = this.querySelector('[data-alan-gorsel="uc"]'), d = this.durum;
      if (!kap) return;
      const p = d.parcalar.find((x) => ['img', 'harf', 'yigin', 'yuva', 'cember', 'buket'].includes(this.ucById[x.uc].render));
      if (!p) {
        const u = this.uclar.find((x) => x.render === 'img'), anahtar = `bos|${u?.id}|${d.metal}`;
        if (kap.dataset.anahtar === anahtar) return;
        kap.dataset.anahtar = anahtar;
        kap.innerHTML = u ? `<img src="${this.adres(`pz-c-${u.id}-${d.metal}.webp`)}" alt="">` : '';
        return;
      }
      const s = p._sprite;
      if (!s || s.anahtar !== this.spriteAnahtari(p)) return;
      const anahtar = `${p.uid}|${s.anahtar}`;
      if (kap.dataset.anahtar === anahtar) return;
      kap.dataset.anahtar = anahtar;
      const o = Math.min(160 / s.w, 160 / s.h, 1), cv = document.createElement('canvas');
      cv.width = Math.round(s.w * o); cv.height = Math.round(s.h * o);
      const c = cv.getContext('2d'); c.imageSmoothingQuality = 'high';
      c.drawImage(s.cv, 0, 0, cv.width, cv.height);
      kap.replaceChildren(cv);
    }

    // Catalogue thumbnails follow the chosen material (a birthstone charm shows a sample stone).
    async ucKartlari() {
      const metal = this.durum.metal;
      for (const u of this.uclar) {
        const cv = this.querySelector(`[data-uc-tuval="${u.id}"]`);
        if (!cv || cv.dataset.metal === metal) continue;
        const ornek = { uid: `k-${u.id}`, uc: u.id, deger: { ...this.bosDeger(u), stone: ORNEK_TAS[u.id] || '', ...(u.render === 'text' ? { [u.fields[0].key]: u.text.sample } : {}), ...(u.ornekDeger || {}) } };
        let s; try { s = await this.sprite(ornek, metal); } catch { continue; }
        const o = Math.min(260 / s.w, 260 / s.h, 1);
        cv.width = Math.round(s.w * o); cv.height = Math.round(s.h * o); cv.dataset.metal = metal;
        const c = cv.getContext('2d'); c.imageSmoothingQuality = 'high';
        c.drawImage(s.cv, 0, 0, cv.width, cv.height);
      }
    }

    // Everything that depends on the state, without rebuilding the charm cards (typing keeps its focus).
    guncelle() {
      const d = this.durum, q = (s) => this.querySelector(s), z = this.zincirById[d.zincir], m = this.veri.metals.find((x) => x.id === d.metal);
      this.querySelectorAll('[data-alan]').forEach((b) => b.setAttribute('aria-selected', String(b.dataset.alan === this.acik)));
      this.querySelectorAll('[data-bolum]').forEach((b) => { b.hidden = b.dataset.bolum !== this.acik; });
      this.querySelectorAll('[data-zincir]').forEach((b) => b.setAttribute('aria-checked', String(b.dataset.zincir === d.zincir)));
      this.querySelectorAll('[data-metal]').forEach((b) => b.setAttribute('aria-checked', String(b.dataset.metal === d.metal)));
      this.querySelectorAll('[data-zincir-gorsel]').forEach((g) => { g.style.backgroundImage = `url("${this.adres(`pz-z-${g.dataset.zincirGorsel}-${d.metal}.webp`)}")`; });
      if (q('[data-boy-izgara]').dataset.zincir !== d.zincir) { this.boylariKur(); q('[data-boy-izgara]').dataset.zincir = d.zincir; }
      this.querySelectorAll('[data-boy]').forEach((b) => b.setAttribute('aria-checked', String(Number(b.dataset.boy) === d.boy)));
      this.querySelectorAll('[data-filtre]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.filtre === this.filtre)));
      this.querySelectorAll('[data-uc]').forEach((k) => { k.hidden = this.filtre !== 'all' && k.dataset.grup !== this.filtre; });
      for (const u of this.uclar) {
        const n = d.parcalar.filter((p) => p.uc === u.id).length, kap = q(`[data-uc-dugme="${u.id}"]`);
        kap.closest('.pz__kart').classList.toggle('is-secili', n > 0);
        kap.innerHTML = n
          ? `<span class="pz__adet"><button type="button" data-is="azalt" aria-label="Remove one ${kacis(u.name)}">−</button><output aria-live="polite">${n}</output><button type="button" data-is="ekle" aria-label="Add another ${kacis(u.name)}">+</button></span>`
          : `<button type="button" class="pz__kart-ekle" data-is="ekle" aria-label="Add ${kacis(u.name)}">Add</button>`;
      }
      // the four selection areas: a small picture and the current choice
      q('[data-alan-deger="uc"]').textContent = d.parcalar.length ? `${d.parcalar.length} selected` : 'Add charms';
      q('[data-alan-deger="zincir"]').textContent = z.name;
      q('[data-alan-deger="metal"]').textContent = m.short;
      q('[data-alan-deger="boy"]').textContent = `${d.boy}″ · ${Math.round(d.boy * 2.54)} cm`;
      q('[data-alan-gorsel="zincir"]').innerHTML = `<span class="pz__alan-zincir" style="background-image:url('${this.adres(`pz-z-${z.id}-${d.metal}.webp`)}');background-size:auto ${((z.h / this.PXMM) * 8).toFixed(1)}px"></span>`;
      q('[data-alan-gorsel="metal"]').innerHTML = `<img src="${this.adres(`pz-c-disc-${d.metal}.webp`)}" alt="">`;
      q('[data-alan-gorsel="boy"]').innerHTML = this.bust(d.boy);
      this.alanUcResmi();

      // summary + total
      q('[data-ozet]').innerHTML = `<li><span>${kacis(z.name)}<small>${kacis(m.name)} · ${d.boy}″</small></span><span>${this.para(this.fiyat(z.sku))}</span></li>` +
        d.parcalar.map((p, i) => { const u = this.ucById[p.uc], a = this.ayrinti(p); return `<li><span>${i + 1}. ${kacis(u.name)}${a ? `<small>${kacis(a)}</small>` : ''}</span><span>${this.para(this.fiyat(this.ucSku(p)))}</span></li>`; }).join('');
      d.parcalar.forEach((p) => { const f = this.querySelector(`[data-parca="${p.uid}"] .pz__parca-fiyat`); if (f) f.textContent = this.para(this.fiyat(this.ucSku(p))); });
      q('[data-ozet-satir]').textContent = [z.name, m.name, `${d.boy}″`, d.parcalar.length ? `${d.parcalar.length} charm${d.parcalar.length > 1 ? 's' : ''}` : 'no charms yet'].join(' · ');
      this.querySelectorAll('[data-toplam]').forEach((t) => { t.textContent = this.para(this.toplam()); });
      q('[data-sahne-not]').hidden = !d.parcalar.some((p) => this.ucById[p.uc].text?.kind !== 'plate');
      this.ucKartlari();
      this.kaydet();
      this.ciz();
    }

    parca(el) { const uid = el.closest('[data-parca]')?.dataset.parca; return this.durum.parcalar.find((p) => p.uid === uid); }

    hataTemizle(p) {
      const kart = this.querySelector(`[data-parca="${p.uid}"]`); if (!kart) return;
      kart.classList.remove('is-hatali');
      const h = kart.querySelector('[data-parca-hata]'); if (h) h.hidden = true;
      this.querySelectorAll('[data-hata]').forEach((g) => { g.hidden = true; });
    }

    alanAc(id) { this.acik = id; this.guncelle(); }

    tikla(e) {
      const t = e.target.closest('[data-is]'); if (!t || !this.contains(t)) return;
      const d = this.durum, is = t.dataset.is;
      if (is === 'alan') return this.alanAc(t.dataset.alan);
      if (is === 'filtre') { this.filtre = t.dataset.filtre; return this.guncelle(); }
      if (is === 'zincir') {
        d.zincir = t.dataset.zincir; d.boy = this.yakinBoy(this.zincirById[d.zincir], d.boy);
        return this.guncelle();
      }
      if (is === 'metal') {
        d.metal = t.dataset.metal; this.guncelle();
        d.parcalar.forEach((p) => this.kartTazele(p));
        return;
      }
      if (is === 'boy') { d.boy = Number(t.dataset.boy); return this.guncelle(); }
      if (is === 'ekle') {
        const uc = this.ucById[t.closest('[data-uc]').dataset.uc];
        const p = { uid: kimlik(), uc: uc.id, deger: this.bosDeger(uc) };
        d.parcalar.push(p);
        this.parcalariKur(); this.guncelle();
        if (uc.fields.length) this.parcayaGit(p.uid);
        return;
      }
      if (is === 'azalt') {
        const id = t.closest('[data-uc]').dataset.uc, i = d.parcalar.map((p) => p.uc).lastIndexOf(id);
        if (i >= 0) d.parcalar.splice(i, 1);
        this.parcalariKur(); return this.guncelle();
      }
      if (is === 'sepete') return this.sepeteEkle();
      if (is === 'yakin') {
        this.yakin = !this.yakin;
        t.setAttribute('aria-pressed', String(this.yakin)); t.textContent = this.yakin ? t.dataset.acik : t.dataset.kapali;
        return this.ciz();
      }

      const p = this.parca(t); if (!p) return;
      const i = d.parcalar.indexOf(p);
      if (is === 'kaldir') { d.parcalar.splice(i, 1); this.parcalariKur(); return this.guncelle(); }
      if (is === 'sola' || is === 'saga') {
        const j = i + (is === 'sola' ? -1 : 1);
        if (j < 0 || j >= d.parcalar.length) return;
        // the two change places, on the chain too when one of them had been slid somewhere; past a name plate the
        // charm takes its place in the row on the other side
        const o = d.parcalar[j], plaka = (x) => this.ucById[x.uc].text?.kind === 'plate';
        if (plaka(p) || plaka(o)) { delete p.konum; delete o.konum; }
        else { const a = p.konum, b = o.konum; delete p.konum; delete o.konum; if (b != null) p.konum = b; if (a != null) o.konum = a; }
        d.parcalar.splice(j, 0, d.parcalar.splice(i, 1)[0]);
        this.parcalariKur(); this.guncelle();
        this.querySelector(`[data-parca="${p.uid}"] [data-is="${is}"]`)?.focus();
        return;
      }
      const kart = t.closest('[data-parca]');
      const sec = (secici, ozellik, deger) => kart.querySelectorAll(secici).forEach((b) => b.setAttribute('aria-checked', String(b.dataset[ozellik] === deger)));
      if (is === 'tas-azalt' || is === 'tas-artir' || is === 'tas-yuva' || is === 'tasc' || is === 'cicekc') {
        const a = this.ucById[p.uc].fields.find((x) => x.type === 'birthstones' || x.type === 'birthflowers'); if (!a) return;
        const v = (p.deger[a.key] ||= ['']);
        if (is === 'tas-artir' && v.length < a.max) { v.push(''); p._aktifTas = v.length - 1; }
        else if (is === 'tas-azalt' && v.length > (a.min || 1)) { v.pop(); p._aktifTas = Math.min(p._aktifTas || 0, v.length - 1); }
        else if (is === 'tas-yuva') p._aktifTas = Number(t.dataset.yuva);
        else if (is === 'tasc' || is === 'cicekc') {
          const k = Math.min(p._aktifTas || 0, v.length - 1); v[k] = is === 'cicekc' ? t.dataset.cicek : t.dataset.tas;
          const sonraki = v.findIndex((x, i) => !x && i > k), bos = sonraki >= 0 ? sonraki : v.findIndex((x) => !x);
          if (bos >= 0) p._aktifTas = bos;                        // straight on to the next stone still without a month
        }
        this.govdeYenile(p, is === 'tasc' ? `[data-is="tasc"][data-tas="${t.dataset.tas}"]` : is === 'cicekc' ? `[data-is="cicekc"][data-cicek="${t.dataset.cicek}"]` : `[data-is="${is}"]${is === 'tas-yuva' ? `[data-yuva="${p._aktifTas}"]` : ''}`);
      }
      else if (is === 'cicek') { p.deger.flower = t.dataset.cicek; sec('[data-cicek]', 'cicek', p.deger.flower); kart.querySelector('[data-cicek-adi]').textContent = this.cicekAdi(p.deger.flower); }
      else if (is === 'tas') {
        const alan = t.closest('[data-alan]') || kart, k = alan.dataset.alan || 'stone';
        p.deger[k] = t.dataset.tas;
        alan.querySelectorAll('[data-tas]').forEach((b) => b.setAttribute('aria-checked', String(b.dataset.tas === p.deger[k])));
        alan.querySelector('[data-tas-adi]').textContent = k === 'stone' ? this.ayrinti(p) : this.tasAdi(p.deger[k]);
      }
      else if (is === 'font') { p.deger.font = t.dataset.font; sec('[data-font]', 'font', p.deger.font); kart.querySelector('[data-font-adi]').textContent = this.fontAdi(p.deger.font); }
      else if (is === 'harf') { p.deger.letter = t.dataset.harf; sec('[data-harf]', 'harf', p.deger.letter); kart.querySelectorAll('[data-font-ornek]').forEach((b) => { b.textContent = p.deger.letter; }); }
      else if (is === 'gorsel-sec') return kart.querySelector('[data-dosya]').click();
      else if (is === 'gorsel-kaldir') { p.gravur = null; this.parcalariKur(); return this.guncelle(); }
      else if (is === 'stil') { p.gravur.ayar.stil = t.dataset.stil; sec('[data-stil]', 'stil', t.dataset.stil); p.gravur.surum++; this.gravurCiz(p); }
      else if (is === 'dondur') { p.gravur.ayar.aci = (p.gravur.ayar.aci + 90) % 360; p.gravur.surum++; this.gravurCiz(p); }
      else if (is === 'ters') { p.gravur.ayar.ters = !p.gravur.ayar.ters; t.setAttribute('aria-pressed', String(p.gravur.ayar.ters)); p.gravur.surum++; this.gravurCiz(p); }
      else return;
      this.hataTemizle(p);
      this.kucukResim(p);
      this.guncelle();
    }

    girdi(e) {
      const t = e.target, p = this.parca(t); if (!p) return;
      const kart = t.closest('[data-parca]');
      if (t.dataset.girdi) {
        if (t.dataset.tur === 'number') t.value = t.value.replace(/\D+/g, '');
        p.deger[t.dataset.girdi] = t.value;
        t.classList.remove('is-invalid');
        t.closest('div').querySelector('[data-sayac]').textContent = `${t.value.length}/${t.maxLength}`;
        const uc = this.ucById[p.uc];
        kart.querySelectorAll('[data-font-ornek]').forEach((b) => { b.textContent = t.value.trim() || uc.text?.sample || 'Abc'; });
        this.hataTemizle(p);
        (this._bekleyen ||= new Set()).add(p);
        clearTimeout(this._yaz);
        this._yaz = setTimeout(() => { this._bekleyen.forEach((x) => this.kucukResim(x)); this._bekleyen.clear(); this.guncelle(); }, 120);
      } else if (t.dataset.kaydirac && p.gravur) {
        p.gravur.ayar[t.dataset.kaydirac] = t.value / 100; p.gravur.hizli = true; p.gravur.surum++;
        this.gravurCiz(p);
      }
    }

    degisti(e) {
      const t = e.target, p = this.parca(t); if (!p) return;
      if (t.dataset.dosya !== undefined) { const f = t.files[0]; t.value = ''; if (f) this.gorselYukle(p, f); }
      else if (t.dataset.kaydirac && p.gravur) { p.gravur.hizli = false; p.gravur.surum++; this.gravurCiz(p); this.kucukResim(p); this.ciz(); }
    }

    // ——————————————————————————————————————————————— photo engraving (per charm)

    async gorselYukle(p, dosya) {
      const kart = this.querySelector(`[data-parca="${p.uid}"]`), hata = kart.querySelector('[data-parca-hata]');
      hata.hidden = true;
      let bmp = null;
      try { bmp = await createImageBitmap(dosya, { imageOrientation: 'from-image' }); }
      catch { try { bmp = await new Promise((ok, h) => { const i = new Image(); i.onload = () => ok(i); i.onerror = h; i.src = URL.createObjectURL(dosya); }); } catch { bmp = null; } }
      if (!bmp) { hata.textContent = 'This image could not be opened. Please try a JPG or PNG.'; hata.hidden = false; return; }
      const k = Math.min(1, 1600 / Math.max(bmp.width, bmp.height));
      const cv = tuval(bmp.width * k, bmp.height * k);
      cv.getContext('2d').drawImage(bmp, 0, 0, cv.width, cv.height);
      // a drawing, handwriting or logo (mostly plain white paper) engraves best as Stamp, a photo as Line Art
      const kc = tuval(64, 64).getContext('2d', { willReadFrequently: true }); kc.drawImage(cv, 0, 0, 64, 64);
      const px = kc.getImageData(0, 0, 64, 64).data; let beyaz = 0;
      for (let j = 0; j < px.length; j += 4) { const mx = Math.max(px[j], px[j + 1], px[j + 2]), mn = Math.min(px[j], px[j + 1], px[j + 2]); if (mx > 205 && mx - mn < 40) beyaz++; }
      p.gravur = { gorsel: cv, dosya, surum: (p.gravur?.surum || 0) + 1, hizli: false, ayar: { stil: beyaz / 4096 > 0.45 ? 'damga' : 'cizgi', detay: 0.45, ters: false, olcek: 1, dx: 0, dy: 0, aci: 0 } };
      this.parcalariKur();
      this.hataTemizle(p);
      this.guncelle();
    }

    // The card's close-up of the charm, with drag to move and pinch / wheel to resize.
    async gravurCiz(p) {
      const kart = this.querySelector(`[data-parca="${p.uid}"]`), cv = kart?.querySelector('[data-gravur-tuval]');
      if (!cv) return;
      const uc = this.ucById[p.uc], g = uc.gravur;
      let s; try { s = await this.sprite(p); } catch { return; }
      const c = cv.getContext('2d'), N = cv.width, yari = g.r * 1.32, o = N / (2 * yari);
      c.clearRect(0, 0, N, N); c.imageSmoothingQuality = 'high';
      c.drawImage(s.cv, g.cx - yari, g.cy - yari, 2 * yari, 2 * yari, 0, 0, N, N);
      cv._cap = 2 * g.r * o;
      if (cv._bagli) return;
      cv._bagli = true;
      const aktif = new Map(); let bas = null;
      const nokta = (e) => { const r = cv.getBoundingClientRect(); return { x: ((e.clientX - r.left) / r.width) * N, y: ((e.clientY - r.top) / r.height) * N }; };
      const kaydirac = () => { const k = kart.querySelector('[data-kaydirac="olcek"]'); if (k) k.value = Math.round(p.gravur.ayar.olcek * 100); };
      const tazele = (hizli) => { p.gravur.hizli = hizli; p.gravur.surum++; this.gravurCiz(p); if (!hizli) { this.kucukResim(p); this.ciz(); } };
      cv.addEventListener('pointerdown', (e) => {
        if (!p.gravur?.gorsel) { kart.querySelector('[data-dosya]').click(); return; }
        cv.setPointerCapture(e.pointerId); aktif.set(e.pointerId, nokta(e));
        bas = { ayar: { ...p.gravur.ayar }, noktalar: new Map(aktif) }; e.preventDefault();
      });
      cv.addEventListener('pointermove', (e) => {
        if (!aktif.has(e.pointerId) || !bas || !p.gravur) return;
        aktif.set(e.pointerId, nokta(e));
        const a = p.gravur.ayar;
        if (aktif.size === 1) {
          const [id, n] = [...aktif][0], n0 = bas.noktalar.get(id); if (!n0) return;
          a.dx = sinir(bas.ayar.dx + (n.x - n0.x) / cv._cap, -0.8, 0.8); a.dy = sinir(bas.ayar.dy + (n.y - n0.y) / cv._cap, -0.8, 0.8);
        } else if (aktif.size === 2) {
          const [n1, n2] = [...aktif.values()], [m1, m2] = [...bas.noktalar.values()]; if (!m1 || !m2) return;
          a.olcek = sinir(bas.ayar.olcek * (Math.hypot(n1.x - n2.x, n1.y - n2.y) / (Math.hypot(m1.x - m2.x, m1.y - m2.y) || 1)), 1, 3); kaydirac();
        }
        tazele(true);
      });
      const bitir = (e) => { if (!aktif.has(e.pointerId)) return; aktif.delete(e.pointerId); bas = aktif.size && p.gravur ? { ayar: { ...p.gravur.ayar }, noktalar: new Map(aktif) } : null; if (!aktif.size && p.gravur) tazele(false); };
      cv.addEventListener('pointerup', bitir); cv.addEventListener('pointercancel', bitir);
      cv.addEventListener('wheel', (e) => {
        if (!p.gravur?.gorsel) return; e.preventDefault();
        p.gravur.ayar.olcek = sinir(p.gravur.ayar.olcek * (e.deltaY < 0 ? 1.06 : 1 / 1.06), 1, 3); kaydirac();
        tazele(true); clearTimeout(cv._teker); cv._teker = setTimeout(() => tazele(false), 300);
      }, { passive: false });
    }

    // ——————————————————————————————————————————————— cart

    // A charm as it was designed, on the stage colour: the picture of its line in the bag and in the order.
    parcaOnizleme(s, boyut) {
      const cv = tuval(boyut, boyut), c = cv.getContext('2d'), o = Math.min((boyut * 0.74) / s.w, (boyut * 0.74) / s.h);
      c.fillStyle = ZEMIN; c.fillRect(0, 0, boyut, boyut);
      c.imageSmoothingQuality = 'high'; c.shadowColor = 'rgba(74,54,34,.24)'; c.shadowBlur = boyut * 0.012; c.shadowOffsetY = boyut * 0.006;
      c.drawImage(s.cv, (boyut - s.w * o) / 2, (boyut - s.h * o) / 2, s.w * o, s.h * o);
      return cv;
    }

    // One request for the whole design: the chain and every charm go into the bag together, or not at all.
    async gonder(satirlar) {
      const kok = window.Shopify?.routes?.root || '/', bolumler = [], fd = new FormData();
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: bolumler } }));
      satirlar.forEach((s, n) => {
        fd.append(`items[${n}][id]`, s.id); fd.append(`items[${n}][quantity]`, '1');
        for (const [k, v] of Object.entries(s.oz)) {
          if (v === '' || v === null || v === undefined) continue;
          if (v instanceof Blob) fd.append(`items[${n}][properties][${k}]`, v, v.name); else fd.append(`items[${n}][properties][${k}]`, String(v));
        }
      });
      fd.append('sections', bolumler.join(',')); fd.append('sections_url', `${kok}variants/${satirlar[satirlar.length - 1].id}`);
      const yanit = await fetch(`${kok}cart/add.js`, { method: 'POST', headers: { 'X-Requested-With': 'XMLHttpRequest' }, body: fd });
      return { yanit, veri: await yanit.json() };
    }

    async sepeteEkle() {
      const d = this.durum, q = (s) => this.querySelector(s);
      // the message shows in the summary box and in the phone's bottom bar
      const genel = (mesaj) => this.querySelectorAll('[data-hata]').forEach((h) => { h.textContent = mesaj; h.hidden = !mesaj; });
      genel('');
      // every charm that needs a personal detail must have it
      let ilk = null, eksikler = 0;
      for (const p of d.parcalar) {
        const mesaj = this.eksik(p), kart = q(`[data-parca="${p.uid}"]`), h = kart.querySelector('[data-parca-hata]');
        kart.classList.toggle('is-hatali', Boolean(mesaj));
        if (h) { h.textContent = mesaj; h.hidden = !mesaj; }
        if (mesaj) { eksikler++; if (!ilk) ilk = p; }
      }
      if (ilk) {
        genel(`Please complete the highlighted ${eksikler > 1 ? 'charms' : 'charm'} before adding your necklace to the bag.`);
        this.parcayaGit(ilk.uid);
        return;
      }

      const dugmeler = [...this.querySelectorAll('[data-is="sepete"]')];
      dugmeler.forEach((b) => { b.setAttribute('aria-busy', 'true'); b.disabled = true; });
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));
      const kok = window.Shopify?.routes?.root || '/';
      let yanit, veri;
      try {
        const z = this.zincirById[d.zincir], m = this.veri.metals.find((x) => x.id === d.metal);
        const kod = Date.now().toString(36).slice(-5).toUpperCase(), tasarim = `${kod}-${kimlik()}`;
        const ozetSatir = d.parcalar.map((p, i) => { const a = this.ayrinti(p); return `${i + 1}. ${this.ucById[p.uc].name}${a ? ` (${a})` : ''}`; });
        const yapi = {
          design: kod, chain: { id: z.id, sku: z.sku, name: z.name, price: this.fiyat(z.sku) / 100 }, material: m.name, length_in: d.boy,
          // chain_offset_mm: where it hangs, along the chain from the middle as seen in the preview (minus = left)
          charms: d.parcalar.map((p, i) => { const u = this.ucById[p.uc], ofs = this._ofs?.[p.uid]; return { position: i + 1, id: u.id, sku: this.ucSku(p), name: u.name, price: this.fiyat(this.ucSku(p)) / 100, ...(Number.isFinite(ofs) ? { chain_offset_mm: Math.round(ofs * 2) / 2, placed_by_customer: p.konum != null } : {}), details: { ...p.deger, ...(p.gravur?.gorsel ? { engraving: p.gravur.ayar } : {}) } }; }),
          total: this.toplam() / 100, currency: window.Shopify?.currency?.active || '',
        };

        // The cart lists the last line first: charms go in from last to first, the chain last, so the necklace
        // reads top to bottom in the bag. Every line carries the design's id, what was chosen and its own picture.
        const satirlar = [];
        for (let i = d.parcalar.length - 1; i >= 0; i--) {
          const p = d.parcalar[i], u = this.ucById[p.uc], oz = { Design: `#${kod}`, Position: `${i + 1} of ${d.parcalar.length}`, Material: m.name };
          if (p.gravur) { p.gravur.hizli = false; p._sprite = null; }       // the finished engraving, not the quick one drawn while dragging
          const s = await this.sprite(p);
          for (const a of u.fields) {
            if (a.type === 'birthstone') { const t = this.veri.stones.find((x) => x.id === p.deger[a.key]); oz[a.label] = `${t.month} – ${t.name}`; }
            else if (a.type === 'birthstones') oz[a.label] = (p.deger[a.key] || []).map((id, k) => `${k + 1}. ${this.tasAdi(id)}`).join(', ');
            else if (a.type === 'birthflower') oz[a.label] = this.cicekAdi(p.deger[a.key]);
            else if (a.type === 'birthflowers') oz[a.label] = (p.deger[a.key] || []).map((id, k) => `${k + 1}. ${this.cicekAdi(id)}`).join(', ');
            else if (a.type === 'engrave-image') {
              const ad = (p.gravur.dosya.name || 'image').replace(/\.[^.]+$/, '').replace(/[^\w-]+/g, '-').slice(0, 40) || 'image';
              oz['Engraving Style'] = STILLER.find((x) => x[0] === p.gravur.ayar.stil)[1] + (p.gravur.ayar.ters ? ' · Inverted' : '');
              // the customer's own file; a very large one goes as the copy the engraving was made from
              oz['_Engraving Image'] = p.gravur.dosya.size <= 4e6 ? p.gravur.dosya : new File([await blob(p.gravur.gorsel, 'image/jpeg', 0.92)], `${ad}.jpg`, { type: 'image/jpeg' });
              oz['_Engraving File'] = new File([await blob(window.GravurMotor.lazer(this.gravurHarita(p, 1400), 2000), 'image/png')], `${ad}-engraving.png`, { type: 'image/png' });
              oz['_Engraving Settings'] = JSON.stringify(p.gravur.ayar);
            } else oz[a.label] = String(p.deger[a.key]).trim();
            if (a.font) oz.Font = this.fontAdi(p.deger.font);
          }
          oz['_Charm Preview'] = new File([await blob(this.parcaOnizleme(s, u.gravur ? 720 : 400), 'image/jpeg', 0.88)], `${u.id}-${kod}-${i + 1}.jpg`, { type: 'image/jpeg' });
          oz['_Design ID'] = tasarim;
          satirlar.push({ id: this.varyant[this.ucSku(p)].id, oz });
        }
        satirlar.push({ id: this.varyant[z.sku].id, oz: {
          Design: `#${kod}`, Material: m.name, Length: `${d.boy}″ (${Math.round(d.boy * 2.54)} cm)`,
          Charms: ozetSatir.length ? ozetSatir.join(' · ') : 'None (chain only)',
          '_Design ID': tasarim, '_Design Total': this.para(this.toplam()), '_Design Summary': JSON.stringify(yapi),
          '_Design Preview': new File([await blob(await this.necklace(1000, { zemin: true, dpr: 1.7, yakin: true }), 'image/jpeg', 0.9)], `necklace-${kod}.jpg`, { type: 'image/jpeg' }),
        } });
        ({ yanit, veri } = await this.gonder(satirlar));
      } catch (hata) {
        veri = { description: hata.message };
      }
      dugmeler.forEach((b) => { b.removeAttribute('aria-busy'); b.disabled = false; });
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));

      if (yanit?.ok) {
        const ayar = window.themeVariables?.settings || {};
        if (ayar.cartType === 'page' || ayar.pageType === 'cart') { window.location.href = `${kok}cart`; return; }
        const sepet = await (await fetch(`${kok}cart.js`)).json();
        sepet.sections = veri.sections;
        this.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: { items: veri.items || [veri], cart: sepet, onSuccessDo: null } }));
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', onSuccessDo: null, cart: sepet } }));
      } else {
        const mesaj = veri?.description || veri?.message || 'Something went wrong. Please try again.';
        genel(mesaj);
        this.dispatchEvent(new CustomEvent('cart:error', { bubbles: true, detail: { error: mesaj } }));
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      }
    }
  }

  customElements.define('pz-builder', PzBuilder);
})();
